// BattleFaceQuoteForm.jsx
// 
// BATTLEFACE TRAVEL INSURANCE QUOTE APPLICATION
// =============================================
// 
// This React component provides a complete travel insurance quote-to-purchase flow
// integrated with BattleFace API and Stripe payment processing.
//
// FLOW OVERVIEW:
// Step 0 (Details)    → API: createQuote()      → Step 1 (Quote Review)
// Step 1 (Quote)      → API: processOrder()     → Step 2 (Order Info)  
// Step 2 (Order)      → API: Payment Process    → Step 3 (Payment)
// Step 3 (Payment)    → API: confirmQuote()     → Step 4 (Confirmation)
//
// KEY FEATURES:
// • Inter font for modern, professional appearance
// • Form validation with user-friendly error messages
// • Phone number auto-formatting and email validation
// • Trip cancellation coverage dropdown (2500, 5000, 10000, 20000, 30000)
// • Coverage amount resets to 2500 when returning to details step
// • Middle name omitted from API if empty (optimization)
// • Stripe payment integration following BattleFace diagram
// • Comprehensive error handling and loading states
// • Responsive Material-UI design
//
// PAYMENT INTEGRATION:
// Follows BattleFace/Stripe integration pattern:
// 1. Create PaymentMethod with Stripe
// 2. Fetch PaymentIntent from BattleFace (GET /orders/{id}/payment/intent/)
// 3. Confirm payment with Stripe using client_secret
// 4. Handle alternative flow if client_secret unavailable
//
// The component uses Material-UI for styling and Stripe for payment processing.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, TextField, MenuItem, Typography, CircularProgress, Alert, Stepper, Step, StepLabel, StepIcon,
  Divider, ThemeProvider, createTheme, Checkbox, FormControlLabel, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, InputAdornment, Switch, Collapse, Accordion, AccordionSummary, AccordionDetails,
  useMediaQuery, Autocomplete, Chip, FormControl, RadioGroup, Radio
} from '@mui/material';
import { Download, Cancel, DirectionsCar, Pets, FlashOn, HomeWork, EventBusy, AccessTime, Luggage, LocalHospital, FlightTakeoff, ExpandMore, Delete, HelpOutline, ArrowBack, ConfirmationNumber } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { loadStripe } from '@stripe/stripe-js';
import FireworksOverlay from './FireworksOverlay';
import { backendBaseUrl } from './api/config';
import { buildPlanEmail } from './data/emailTemplate';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Import theme configuration
import { THEMES, CURRENT_THEME } from './themes/themeConfig';
import defaultTheme from './themes/defaultTheme';
import orangeTheme from './themes/orangeTheme';
import tradeshowTheme from './themes/tradeshowTheme';
import ticketCoverageTheme from './themes/ticketCoverageTheme';
import { CssBaseline } from '@mui/material';
import COUNTRIES, { COUNTRIES as COUNTRIES_NAMED } from './data/countries';

// Marketing content URLs configuration with environment-based URLs
const getMarketingBaseUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://landsman-tradeshowcoverage-dev.azurewebsites.net';
  }
  if (hostname.includes('staging')) {
      return 'https://landsman-tradeshowcoverage-dev.azurewebsites.net';
  }
    return 'https://tradeshowcoverage.com';
};

const MARKETING_URLS = {
  // Default URL used for any theme that doesn't have a specific override
    default: 'https://tradeshowcoverage.com/marketing.html',

  // Theme-specific overrides (set to null to use default)
  orange: null,  // Uses default
    tradeshow: null, //User default
  ticketcoverage: 'https://ticketcoverage.com/marketing.html',
};

// Stripe configuration and API settings
// These settings came from BattleFace for development/testing
// (Removed custom DatePicker action bar to restore default dialog formatting)

// Configuration-driven environment sourced from Azure App Service app settings via config
const backendBase = backendBaseUrl;
const stripePromise = loadStripe(process.env?.REACT_APP_STRIPE_PUBLIC_KEY);
const BATTLEFACE_API_TOKEN = process.env?.REACT_APP_BATTLEFACE_API_TOKEN;


// Minimal wrapper to use throughout the form. Keeps existing usages intact.
const AutoCloseDatePicker = (props) => {
  return <DatePicker {...props} />;
};



// Stripe CardElement styling to match our Inter font theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
};

// PaymentForm Component - Handles the Stripe payment process
// This component is wrapped by Stripe Elements and handles the final payment step.
// It follows the BattleFace/Stripe integration flow:
// 1. Create a PaymentMethod with Stripe using card details
// 2. Fetch PaymentIntent from BattleFace (GET request, no body)
// 3. Use client_secret from BattleFace to confirm payment with Stripe
// 4. Handle success or error responses
const PaymentForm = ({ 
  quote, 
  tripCostCoverage, 
  numTravelers, 
  orderNumber, 
  applicants,
  onPaymentSuccess, 
  onPaymentError, 
  loading, 
  setLoading,
  displayAmount,
  displayCurrency
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState('');
  // Terms and conditions state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [destinationDisclosureAccepted, setDestinationDisclosureAccepted] = useState(false);

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setPaymentError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card element not found. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setPaymentError('');

    try {
      // Get main applicant's name for billing details
      const mainApplicant = applicants?.find(app => app.is_main) || applicants?.[0] || {};
      const customerName = `${mainApplicant.first_name || ''} ${mainApplicant.last_name || ''}`.trim() || 'Customer';

      // Step 1a: Create PaymentMethod via Stripe API
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerName,
          email: mainApplicant.email || '',
          phone: mainApplicant.contact_number?.replace(/\D/g, '') || '', // Send digits only
        },
      });

      if (paymentMethodError) {
        setPaymentError(paymentMethodError.message);
        onPaymentError(paymentMethodError.message);
        return;
      }

      // console.log('PaymentMethod created:', paymentMethod.id);

      // Step 1b: Fetch PaymentIntent from BattleFace API
      // This follows the BattleFace/Stripe integration diagram:
      // - Use GET request (no body) to retrieve payment intent
      // - Extract client_secret from response for Stripe confirmation
      let clientSecret = null;
      
      try {
        const paymentIntentResponse = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderNumber}/payment/intent/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': BATTLEFACE_API_TOKEN
          }
          // Note: No body for GET request
        });

          console.log(orderNumber);

        if (paymentIntentResponse.ok) {
          const paymentIntentData = await paymentIntentResponse.json();
          console.log('BattleFace PaymentIntent Response:', paymentIntentData);

          // Extract the client_secret from the response
          clientSecret = paymentIntentData.client_secret || 
                        paymentIntentData.data?.client_secret || 
                        paymentIntentData.intent?.client_secret;
        } else {
          // console.log('PaymentIntent endpoint not available, will use alternative flow');
        }
      } catch (intentError) {
        // console.log('PaymentIntent fetch failed, will use alternative flow:', intentError.message);
      }

      // Step 2: Confirm payment with Stripe using the client_secret from BattleFace
      // Validate the client_secret before attempting confirmation
      const isValidClientSecret = typeof clientSecret === 'string' && clientSecret.includes('_secret_');
      if (isValidClientSecret) {
         console.log('Using client_secret:', clientSecret);

        // Use Stripe's confirmCardPayment with the PaymentMethod and client_secret
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id
          }
        );

        if (confirmError) {
          setPaymentError(confirmError.message);
          onPaymentError(confirmError.message);
        } else if (paymentIntent.status === 'succeeded') {
          // console.log('Payment succeeded:', paymentIntent.id);
          onPaymentSuccess(paymentIntent);
        } else {
          setPaymentError(`Payment status: ${paymentIntent.status}`);
          onPaymentError(`Payment status: ${paymentIntent.status}`);
        }
      } else {
        // If the intent response didn't include a usable client_secret, do not attempt confirmation
        // Common cause: using a payment_intent ID instead of its client_secret or an expired/invalid intent
        throw new Error('Unable to retrieve a valid client_secret for this order. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.message || 'An unexpected error occurred';
      setPaymentError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit}>
      <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius={2}>
        <Typography variant="h6" mb={2}>Payment Details</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Secure payment processing powered by Stripe
        </Typography>
        
        <Box mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
          <CardElement options={cardElementOptions} />
        </Box>

        {paymentError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {paymentError}
          </Alert>
        )}

        {/* Terms & Conditions Section */}
        <Box mb={3}>
          <Typography variant="h6" mb={2} fontWeight="bold">
            TERMS & CONDITIONS
          </Typography>
          
          {/* Electronic Signature Checkbox */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  sx={{ alignSelf: 'flex-start', mt: 0 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  Checking here constitutes my electronic signature. I confirm that I have read, understand 
                  and agree to the{' '}
                  <Box 
                    component="span" 
                    sx={{ 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontWeight: 'bold'
                    }}
                    onClick={() => {
                      // Open terms and conditions in new tab/window
                      window.open('https://partner.battleface.com/library/landsman-elite-multi-annual', '_blank');
                    }}
                  >
                    policy terms and conditions
                  </Box>
                  , all &nbsp;
                  
                                    <Box 
                    component="span" 
                    sx={{ 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontWeight: 'bold'
                    }}
                    onClick={() => {
                      // Open terms and conditions in new tab/window
                      window.open('https://www.battleface.com/documents/important-notices-and-disclosures-spi.pdf', '_blank');
                    }}
                  >
                    important disclosures
                  </Box>, 
                  which includes fraud warnings, privacy notice, and consent to electronic signature and delivery.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
          </Box>

          {/* Destination Disclosure Checkbox */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={destinationDisclosureAccepted}
                  onChange={(e) => setDestinationDisclosureAccepted(e.target.checked)}
                  sx={{ alignSelf: 'flex-start', mt: 0 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    By checking here, I understand that the Single-Trip Plan will not cover 
                    me for travel to certain destinations.{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        textDecoration: 'underline', 
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 'medium'
                      }}
                      onClick={() => {
                        // Open link in new tab/window
                        window.open('https://library.battleface.com/ineligible-destinations/', '_blank');
                      }}
                    >
                      View the full list of destinations for which we do not offer coverage.
                    </Box>
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </Box>

        <Button 
          type="submit" 
          variant="contained" 
          color="success" 
          fullWidth 
          disabled={!stripe || loading || !termsAccepted || !destinationDisclosureAccepted}
          sx={{ mt: 2 }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              Processing Payment...
            </Box>
          ) : (
            `Pay $${(Number(displayAmount) || 0).toFixed(2)}`
          )}
        </Button>
      </Box>
    </form>
  );
};


const COUNTRY_LIST = COUNTRIES_NAMED || COUNTRIES;

const STATE_LIST = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

// Define the 6-step process for travel insurance quote and purchase
const steps = [
  'Residence Info', // Step 0: Country of residence and state (US only)
  'Event Details',  // Step 1: Ticket details and branching
  'Travel Details', // Step 2: Collect trip and traveler information
  'Quote',          // Step 3: Display quote with coverage options and pricing
  'Review Order',   // Step 4: Collect applicant details and create order
  'Payment',        // Step 5: Process payment through Stripe/BattleFace
  'Download'        // Step 6: Show confirmation and policy details
];

// Custom StepIcon component that shows a download icon for the final step
// Only shows download icon when actively on step 6, otherwise shows number
const CustomStepIcon = (props) => {
  const { icon, active, completed } = props;
  
  // Show download icon only for final step AND when it's the active step
  if (icon === steps.length && active) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText'
        }}
      >
        <Download sx={{ fontSize: 16 }} />
      </Box>
    );
  }
  
  // Use default StepIcon for all other steps (including step 5 when not active)
  return <StepIcon {...props} />;
};

// Helper function to calculate date of birth from age
// BattleFace API requires DOB, but our form collects age for simplicity
// Uses January 1st as default birth date for the calculated year
const calculateDateOfBirth = (age) => {
  const today = new Date();
  const birthYear = today.getFullYear() - age;
  return `${birthYear}-01-01`; // Format: YYYY-MM-DD
};

// Helper function to clean applicant data for API submission
// Removes middle_name field if empty (API optimization)
// Transforms form data into the format expected by BattleFace API
const cleanApplicantData = (applicant, index, travelerAges, tripCostCoverage) => {
  const cleanedData = {
    first_name: applicant.first_name,
    last_name: applicant.last_name,
    email: applicant.email,
    contact_number: applicant.contact_number || '',
    address: applicant.address || '',
    city: applicant.city || '',
    post_code: applicant.post_code,
    country_code: applicant.country_code,
    state_code: applicant.state_code,
    is_main: applicant.is_main,
    dob: calculateDateOfBirth(parseInt(travelerAges[index])),
    trip_cost: tripCostCoverage
  };

  // Only include middle_name if it's not empty
  if (applicant.middle_name && applicant.middle_name.trim() !== '') {
    cleanedData.middle_name = applicant.middle_name;
  }

  return cleanedData;
};

// Helper function to format phone number
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digitsOnly.substring(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length >= 6) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
  } else if (limitedDigits.length >= 3) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
  } else if (limitedDigits.length > 0) {
    return `(${limitedDigits}`;
  }
  return '';
};

// Helper function to validate email format
const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return { isValid: false, message: 'Email required' };
  }

  const trimmedEmail = email.trim().toLowerCase();
  
  // Basic format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }
  
  // Length validation
  if (trimmedEmail.length < 5) {
    return { isValid: false, message: 'Email address is too short' };
  }
  
  if (trimmedEmail.length > 254) {
    return { isValid: false, message: 'Email address is too long' };
  }
  
  // Additional format checks
  if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.') || 
      trimmedEmail.startsWith('@') || trimmedEmail.endsWith('@')) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  if (trimmedEmail.includes('..') || trimmedEmail.includes('@.') || trimmedEmail.includes('.@')) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  // Check for valid domain
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return { isValid: false, message: 'Email must contain exactly one @' };
  }
  
  const [localPart, domain] = parts;
  if (localPart.length === 0 || domain.length === 0) {
    return { isValid: false, message: 'Email must have both username and domain' };
  }
  
  if (!domain.includes('.')) {
    return { isValid: false, message: 'Email domain must contain a dot' };
  }
  
  const domainParts = domain.split('.');
  if (domainParts.some(part => part.length === 0)) {
    return { isValid: false, message: 'Invalid domain format' };
  }
  
  return { isValid: true, message: '' };
};

// Helper function to find the first allowable date
const findFirstAllowableDate = () => {
  const minStart = new Date();
  // Start date must be at least 2 days after current date
  minStart.setDate(minStart.getDate() + 2);
  minStart.setHours(0, 0, 0, 0);

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 180);

  // Check if minStart is within the allowable range
  if (minStart <= maxDate) {
    return minStart;
  }

  // If somehow minStart is beyond max date, return null
  return null;
};

// Helper function to get Travel Medical coverage amount based on state
// New Hampshire (NH) has a lower coverage limit of $50,000
// All other states have the standard $250,000 coverage
const getTravelMedicalCoverage = (stateCode) => {
  if (stateCode === 'NH') {
    return { amount: '$50,000', value: 50000 };
  }
  return { amount: '$100,000', value: 100000 };
};

// Main BattleFaceQuoteForm Component
// This is the primary React component that manages the entire quote-to-purchase flow.
// The component uses a stepper UI pattern with 5 steps:
// 0: Details - Collect trip information and traveler details
// 1: Quote - Display quote results and coverage options  
// 2: Review Order - Collect applicant personal information
// 3: Payment - Process payment through Stripe/BattleFace
// 4: Download - Show confirmation and policy details
export default function BattleFaceQuoteForm() {
  // Theme state - Initialize from URL parameter if available
  const [themeName, setThemeName] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const themeParam = params.get('theme');
      if (themeParam && Object.values(THEMES).includes(themeParam.toLowerCase())) {
        return themeParam.toLowerCase();
      }
    } catch (e) {
      console.error('Error parsing theme parameter:', e);
    }
    return CURRENT_THEME;
  });
  const themeMap = {
    [THEMES.DEFAULT]: defaultTheme,
    [THEMES.ORANGE]: orangeTheme,
    [THEMES.TRADESHOW]: tradeshowTheme,
    [THEMES.TICKETCOVERAGE]: ticketCoverageTheme,
  };
  const theme = themeMap[themeName] || defaultTheme;
  // Step management - tracks which step of the process the user is on
  const [activeStep, setActiveStep] = useState(0);
  // Event details state
  const [conferenceStart, setConferenceStart] = useState(null);
  const [conferenceEnd, setConferenceEnd] = useState(null);
  const [conferencePrice, setConferencePrice] = useState('');
  const [ticketPurchaseDate, setTicketPurchaseDate] = useState(null);
  const [singleTravelerAge, setSingleTravelerAge] = useState('');
  const [insureAllTravel, setInsureAllTravel] = useState(true);
  // New Event Details fields
  const [eventName, setEventName] = useState('');
  const [eventCity, setEventCity] = useState('');
  const [attendeeRole, setAttendeeRole] = useState('');

  // Read marketing / affiliate / session parameters from URL  
  const [mcc, setMcc] = useState(null); // marketing channel code (required)
  const [mccAuthResult, setMccAuthResult] = useState(null); // store authorization response
  const [mccAuthError, setMccAuthError] = useState(null);

  // Marketing channel enforcement flag (missing OR inactive OR auth failed)
  const isMccMissing = (mcc === null) || !!mccAuthError || (mccAuthResult && mccAuthResult.IsMarketingChannelActive === false);
  // Dynamic state options strictly from marketing channel (no static fallback)
  const stateOptions = isMccMissing
    ? []
    : (mccAuthResult?.availableStates?.length
        ? mccAuthResult.availableStates.map(s => ({ code: s.code, name: s.name }))
            : []);


  // Guarded step setter to prevent advancing past step 0 when MCC missing
  const guardedSetActiveStep = (next) => {
    if (isMccMissing && next > 0) return; // block progression
    setActiveStep(next);
  };
  useEffect(() => {
    // Parse other params from query param (theme is now parsed in useState initialization)
    const params = new URLSearchParams(window.location.search);

    const mccParam = params.get('mcc');
    if (mccParam && mccParam.trim() !== '') {
      setMcc(mccParam.trim());
    } else {
      setMcc(null); // explicitly null if missing or empty
    }

    // Call marketing channel authorization early if present
    (async () => {
      try {
        if (mccParam && mccParam.trim() !== '') {
          const authData = await AuthorizeMarketingChannel(mccParam.trim());
          setMccAuthResult(authData || null);
          if (!authData) {
            setMccAuthError('Marketing channel could not be authorized.');
          }
        }
      } catch (e) {
        setMccAuthError(e.message || 'Failed to authorize marketing channel.');
      }
    })();

    const quoteIdParam = params.get('quoteid') || params.get('quoteId');
    if (quoteIdParam) {
      // Fetch quote data from Azure tracking endpoint
      (async () => {
        try {         

          const res = await RetrieveQuote(quoteIdParam);          
          
          if(res == "")
          {
            console.log("No quote found for the provided quoteId");
          }

              var originalResponse = res.response.data;                     

            //console.log(originalResponse);

              setQuote(originalResponse.quoted_price || 0);
              // Set applicants state if present in the response

              setHomeCountryState(originalResponse.host_country_state);           
              setTripCostCoverage(originalResponse.applicants[0].trip_cost);   
              
              
              if (originalResponse.applicants) {
                                
                originalResponse.applicants[0].first_name = '';
                originalResponse.applicants[0].last_name = '';
                originalResponse.applicants[0].middle_name = '';
                originalResponse.applicants[0].email = '';
                originalResponse.applicants[0].country_code = 'US';
                originalResponse.applicants[0].contact_number = '';
                originalResponse.applicants[0].address = '';
                originalResponse.applicants[0].city = '';
                originalResponse.applicants[0].post_code = '';                
                originalResponse.applicants[0].state_code = originalResponse.host_country_state;
                  
                setTripStart(new Date(originalResponse.start_date));
                setTripEnd(new Date(originalResponse.end_date));
                setApplicants(originalResponse.applicants);
                  setNumTravelers(originalResponse.applicants.length);

                  // Ensure we use the full details array from the quote response
                  const details = Array.isArray(originalResponse.details)
                    ? originalResponse.details
                    : (Array.isArray(originalResponse.items) && originalResponse.items[0]?.details) || [];

                  // Toggle optional add-ons based on order details (robust alias mapping)
                  if (Array.isArray(details) && details.length) {
                      const mapDetailToAlias = (d) => {
                          // Prefer identifying by limit 'for' field when present
                          const forKey = String(d?.for || '').toLowerCase();
                          if (forKey.includes('vacation_rental_damage')) return 'vacation_rental_damage';
                          if (forKey.includes('non_flight_accidental_death_and_dismemberment')) return 'accidental_death_and_dismemberment';
                          // Fallback to other common fields
                          const candidate = String(d?.package_alias || d?.detail || d?.id || d?.product_package_id || '').toLowerCase();
                          if (!candidate) return null;
                          if (candidate.includes('cancel_for_any_reason') || candidate.includes('cancel for any reason')) return 'cancel_for_any_reason';
                          if (candidate.includes('rental_vehicle_damage') || candidate.includes('rental vehicle damage')) return 'rental_vehicle_damage';
                          if (candidate.includes('vacation_rental_damage') || candidate.includes('vacation rental damage')) return 'vacation_rental_damage';
                          if (candidate.includes('accidental_death') || candidate.includes('accidental death')) return 'accidental_death_and_dismemberment';
                          if (candidate.includes('pet_travel_package') || candidate.includes('pet travel')) return 'pet_travel_package';
                          return null;
                      };
                      const toggles = {};
                      OPTIONAL_ALIASES.forEach(alias => { toggles[alias] = false; });
                      details.forEach(d => {
                          const alias = mapDetailToAlias(d);
                          if (alias) toggles[alias] = true;
                      });

                      // Attempt to rehydrate selected limits (if present in details)
                      // Look specifically for type === 'limit' entries keyed by 'for'
                      const adndDetail = details.find(d => {
                        const type = String(d?.type || '').toLowerCase();
                        const forKey = String(d?.for || '').toLowerCase();
                        // Payload uses type: 'benefit_limit' and for: 'non_flight_accidental_death_and_dismemberment'
                        return type === 'benefit_limit' && forKey === 'non_flight_accidental_death_and_dismemberment';
                      });
                      const vrdDetail = details.find(d => {
                        const type = String(d?.type || '').toLowerCase();
                        const forKey = String(d?.for || '').toLowerCase();
                        return type === 'benefit_limit' && forKey === 'vacation_rental_damage';
                      });
                      const vrdReservationsDetail = details.find(d => {
                        const type = String(d?.type || '').toLowerCase();
                        const forKey = String(d?.for || '').toLowerCase();
                        return type === 'reservations' && forKey === 'vacation_rental_damage';
                      });
                      // Parse limit value from either 'value' or 'benefit_limit'
                      const parseLimit = (d) => {
                          if (!d) return null;
                          const toNumber = (x) => {
                              if (x === null || typeof x === 'undefined') return NaN;
                              if (typeof x === 'number') return x;
                              // Handle formatted strings like "$100,000" or "100,000"
                              if (typeof x === 'string') {
                                  const cleaned = x.replace(/[^0-9.]/g, '');
                                  return Number(cleaned);
                              }
                              return Number(x);
                          };
                          if (typeof d.value !== 'undefined') {
                              const v = toNumber(d.value);
                              return isNaN(v) ? null : v;
                          }
                          if (typeof d.benefit_limit !== 'undefined') {
                              const v = toNumber(d.benefit_limit);
                              return isNaN(v) ? null : v;
                          }
                          return null;
                      };
                      const adndSelected = parseLimit(adndDetail);
                      const vrdSelected = parseLimit(vrdDetail);
                      const normalizeVrdLimit = (val) => {
                          if (typeof val !== 'number' || isNaN(val)) return null;
                          const allowed = [1500, 3000, 5000];
                          if (allowed.includes(val)) return val;
                          return allowed.reduce((prev, curr) => Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev);
                      };
                      const normalizeAdndLimit = (val) => {
                          if (typeof val !== 'number' || isNaN(val)) return null;
                          const allowed = [100000, 250000, 500000];
                          // Pick exact match if available; else choose closest
                          if (allowed.includes(val)) return val;
                          return allowed.reduce((prev, curr) => Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev);
                      };
                      const normalizedAdnd = normalizeAdndLimit(adndSelected);
                      if (typeof adndSelected === 'number') {
                          setAdndLimit(normalizedAdnd ?? adndSelected);
                          toggles.accidental_death_and_dismemberment = true;
                      }
                      if (typeof vrdSelected === 'number') {
                          const normalizedVrd = normalizeVrdLimit(vrdSelected);
                          setVrdLimit(normalizedVrd ?? vrdSelected);
                          toggles.vacation_rental_damage = true;
                      }
                      if (vrdReservationsDetail && typeof vrdReservationsDetail.amount !== 'undefined') {
                        const qty = Number(vrdReservationsDetail.amount);
                        if (!isNaN(qty) && qty > 0) {
                          setVrdReservations(qty);
                          toggles.vacation_rental_damage = true;
                        }
                      }
                      setOptionalSelections(toggles);
                      // If CFAR is included, rehydrate deposit date from the quote
                      if (toggles.cancel_for_any_reason && originalResponse.deposit_date) {
                        const d = new Date(originalResponse.deposit_date);
                        if (!isNaN(d)) setCfarDepositDate(d);
                      }
                  }

                // Rehydrate selected destinations from quote response (states and countries)
                if (Array.isArray(originalResponse.destinations) && originalResponse.destinations.length) {
                  try {
                    const mapped = originalResponse.destinations
                      .map((d) => {
                        // Normalize destination object
                        const dest = (typeof d === 'string') ? { country_code: d } : (d || {});
                        const cCode = dest.country_code || dest.code || '';
                        const sCode = dest.state_code || dest.state || '';
                        // Try to find matching option from destinationOptions
                        if (sCode && cCode === 'US') {
                          return destinationOptions.find(o => o.type === 'state' && o.stateCode === sCode) || null;
                        }
                        return destinationOptions.find(o => o.type === 'country' && o.countryCode === cCode) || null;
                      })
                      .filter(Boolean);
                    if (mapped.length) setSelectedDestinations(mapped);
                  } catch {}
                }
                // Set travelerAges from applicants' dob if available
                const ages = originalResponse.applicants.map(app => {
                  if (app.dob) {
                    // Expecting dob in YYYY-MM-DD format
                    const birthYear = parseInt(app.dob.split('-')[0], 10);
                    const thisYear = new Date().getFullYear();
                    return (thisYear - birthYear).toString();
                  }
                  return '';
                });
                setTravelerAges(ages);                              
              }
              // Extract quoteId from the returned data (try multiple fields)
              const extractedQuoteId = res.quoteId || res.id || res.quote_id ||
                res.data?.id || originalResponse.quote_id || originalResponse.quote_number || quoteIdParam;
              setQuoteId(extractedQuoteId);
              setActiveStep(3); // Move to Quote Review step
            } catch (error) {
              console.error('Error fetching quote data:', error);
            }          
          })();
    }

    const orderIdParam = params.get('orderid') || params.get('orderId');
    if (orderIdParam) {
      // Fetch quote data from Azure tracking endpoint
      (async () => {
        try {         

          const res = await RetrieveOrder(orderIdParam);          
          
          if(res == "")
          {
            console.log("No quote found for the provided quoteId");
          }
              // Normalize order response
              const orderData = res;      

              // Set quote summary (price, currency, details for amounts)
              setQuote({
                data: {
                  quoted_price: orderData.premium ?? orderData.total ?? 0,
                  currency_id: orderData.currency_id || 'USD',
                  details: orderData.items?.[0]?.details || []
                }
              });
              
              // Extract quote id from order
              const extractedQuoteId = orderData.quote_id || orderData.quoteId || orderData.id || quoteIdParam;
              setQuoteId(extractedQuoteId);
              setOrderNumber(orderIdParam);

              // Rehydrate dates from the order item
              const firstItem = Array.isArray(orderData.items) ? orderData.items[0] : null;
              let startDateLocal = null;
              if (firstItem) {
                if (firstItem.start_at) {
                  const d = new Date(firstItem.start_at);
                  if (!isNaN(d)) { setTripStart(d); startDateLocal = d; }
                }
                if (firstItem.end_at) {
                  const d = new Date(firstItem.end_at);
                  if (!isNaN(d)) setTripEnd(d);
                }
                if (firstItem.deposit_at) {
                  const d = new Date(firstItem.deposit_at);
                  if (!isNaN(d)) setCfarDepositDate(d);
                }
              }

              // Rehydrate selected destinations from order response
              try {
                const destSrc = Array.isArray(orderData.destinations) && orderData.destinations.length
                  ? orderData.destinations
                  : (Array.isArray(firstItem?.order?.destinations) ? firstItem.order.destinations : []);
                if (Array.isArray(destSrc) && destSrc.length) {
                  const mapped = destSrc
                    .map((d) => {
                      const dest = (typeof d === 'string') ? { country_code: d } : (d || {});
                      const cCode = dest.country_code || dest.code || '';
                      const sCode = dest.state_code || dest.state || '';
                      if (sCode && cCode === 'US') {
                        return destinationOptions.find(o => o.type === 'state' && o.stateCode === sCode) || null;
                      }
                      return destinationOptions.find(o => o.type === 'country' && o.countryCode === cCode) || null;
                    })
                    .filter(Boolean);
                  if (mapped.length) setSelectedDestinations(mapped);
                }
              } catch {}

              // Trip cost from details (type === 'trip_cost')
              const details = firstItem?.details || [];
              const tripCostEntry = details.find(d => (d?.type || '').toLowerCase() === 'trip_cost');
              let coverageAmtLocal = null;
              if (tripCostEntry && typeof tripCostEntry.amount !== 'undefined') {
                const amt = Number(tripCostEntry.amount);
                if (!isNaN(amt)) { const amtStr = String(amt); setTripCostCoverage(amtStr); coverageAmtLocal = amtStr; }
              }

              // Rehydrate applicant info
              const mainApplicant = orderData.applicant || firstItem?.order?.applicant || {};
              if (mainApplicant && (mainApplicant.first_name || mainApplicant.last_name || mainApplicant.email)) {
                setApplicants([
                  {
                    first_name: mainApplicant.first_name || '',
                    middle_name: '',
                    last_name: mainApplicant.last_name || '',
                    email: mainApplicant.email || '',
                    contact_number: mainApplicant.contact_number || '',
                    address: mainApplicant.address || '',
                    city: mainApplicant.city || '',
                    post_code: mainApplicant.post_code || '',
                    country_code: mainApplicant.country_code || 'US',
                    state_code: mainApplicant.state_code || '',
                    is_main: true
                  }
                ]);
                if (mainApplicant.state_code) setHomeCountryState(mainApplicant.state_code);
              }

              // Rehydrate traveler ages (best effort from DOB)
              const insured = firstItem?.insured;
              if (insured?.dob) {
                const birthYear = parseInt(String(insured.dob).split('-')[0], 10);
                const thisYear = new Date().getFullYear();
                const age = (thisYear - birthYear);
                if (!isNaN(age)) {
                  setNumTravelers(1);
                  setTravelerAges([String(age)]);
                }
              }

              // Toggle optional add-ons based on order details (robust alias mapping)
              if (Array.isArray(details) && details.length) {
                const mapDetailToAlias = (d) => {
                  // Prefer identifying by limit 'for' field when present
                  const forKey = String(d?.for || '').toLowerCase();
                  if (forKey.includes('vacation_rental_damage')) return 'vacation_rental_damage';
                  if (forKey.includes('non_flight_accidental_death_and_dismemberment')) return 'accidental_death_and_dismemberment';
                  // Fallback to other common fields
                  const candidate = String(d?.package_alias || d?.detail || d?.id || d?.product_package_id || '').toLowerCase();
                  if (!candidate) return null;
                  if (candidate.includes('cancel_for_any_reason') || candidate.includes('cancel for any reason')) return 'cancel_for_any_reason';
                  if (candidate.includes('rental_vehicle_damage') || candidate.includes('rental vehicle damage')) return 'rental_vehicle_damage';
                  if (candidate.includes('vacation_rental_damage') || candidate.includes('vacation rental damage')) return 'vacation_rental_damage';
                  if (candidate.includes('accidental_death') || candidate.includes('accidental death')) return 'accidental_death_and_dismemberment';
                  if (candidate.includes('pet_travel_package') || candidate.includes('pet travel')) return 'pet_travel_package';
                  return null;
                };
                const toggles = {};
                OPTIONAL_ALIASES.forEach(alias => { toggles[alias] = false; });
                details.forEach(d => {
                  const alias = mapDetailToAlias(d);
                  if (alias) toggles[alias] = true;
                });

                // Attempt to rehydrate selected limits (if present in details)
                // Look specifically for type === 'limit' entries keyed by 'for'
                const adndDetail = details.find(d => String(d?.type || '').toLowerCase() === 'limit' && String(d?.for || '').toLowerCase().includes('non_flight_accidental_death_and_dismemberment'));
                const vrdDetail = details.find(d => String(d?.type || '').toLowerCase() === 'limit' && String(d?.for || '').toLowerCase().includes('vacation_rental_damage'));
                // Parse limit value from either 'value' or 'benefit_limit'
                const parseLimit = (d) => {
                  if (!d) return null;
                  if (typeof d.value !== 'undefined') {
                    const v = Number(d.value);
                    return isNaN(v) ? null : v;
                  }
                  if (typeof d.benefit_limit !== 'undefined') {
                    const v = Number(d.benefit_limit);
                    return isNaN(v) ? null : v;
                  }
                  return null;
                };
                const adndSelected = parseLimit(adndDetail);
                const vrdSelected = parseLimit(vrdDetail);
                if (typeof adndSelected === 'number') {
                  setAdndLimit(adndSelected);
                  toggles.accidental_death_and_dismemberment = true;
                }
                if (typeof vrdSelected === 'number') {
                  setVrdLimit(vrdSelected);
                  toggles.vacation_rental_damage = true;
                }
                setOptionalSelections(toggles);
              }

              // If start date is in the past, show exception and return to Step 0
              if (startDateLocal) {
                const today = new Date();
                const sd = new Date(startDateLocal.getTime());
                sd.setHours(0,0,0,0);
                today.setHours(0,0,0,0);
                if (sd.getTime() < today.getTime()) {
                  setPastDateMessage('Your trip start date has already passed. Please start a new quote.');
                  setPastDateDialogOpen(true);
                  setActiveStep(0);
                  return;
                }
              }

              // Run a requote based on rehydrated coverage before confirming
              if (coverageAmtLocal) {
                await requoteWithNewCoverage(coverageAmtLocal);
              }

              // Confirm quote for this order and move to Payment step
              await confirmQuote(extractedQuoteId, orderIdParam);
              setActiveStep(5); // Move to Payment step
            } catch (error) {
              console.error('Error fetching quote data:', error);
            }          
          })();
    }
             


  }, []);



  // User input states - stores form data entered by the user
  const [tripStart, setTripStart] = useState(null);           // Trip start date
  const [tripEnd, setTripEnd] = useState(null);               // Trip end date (auto-calculated)
  const [selectedDestinations, setSelectedDestinations] = useState([]); // Multiple destinations (states/countries)
  const [countryOfResidence, setCountryOfResidence] = useState('US'); // Country of residence
  const [homeCountryState, setHomeCountryState] = useState(''); // User's home state
  const [numTravelers, setNumTravelers] = useState(1);        // Number of travelers (1-10)
  const [travelerAges, setTravelerAges] = useState(['']);     // Array of traveler ages
  // Build combined destination options and codes after state is declared
  const destinationOptions = useMemo(() => {
    const states = (STATE_LIST || []).map(s => ({
      label: `${s.name} (United States)`,
      type: 'state',
      countryCode: 'US',
      stateCode: s.code
    }));
    const countries = (COUNTRY_LIST || []).map(c => ({
      label: c.name,
      type: 'country',
      countryCode: c.code
    }));
    return [...states, ...countries];
  }, []);

  const destinationCountryCodes = useMemo(() => {
    const codes = (selectedDestinations || []).map(d => d.countryCode);
    return Array.from(new Set(codes));
  }, [selectedDestinations]);
  
  // State for the ineligible state dialog
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [selectedIneligibleState, setSelectedIneligibleState] = useState('');
  const [tripCostCoverage, setTripCostCoverage] = useState(''); // Trip amount entered by user (required)
  // Dialog for past start date on rehydration
  const [pastDateDialogOpen, setPastDateDialogOpen] = useState(false);
  const [pastDateMessage, setPastDateMessage] = useState('');

  // API and UX states - manages API responses and loading states
  const [loading, setLoading] = useState(false);              // Loading indicator
  const [quote, setQuote] = useState(null);                   // Quote response from BattleFace
  const [quoteId, setQuoteId] = useState(null);               // Quote ID for subsequent API calls
  const [orderNumber, setOrderNumber] = useState(null);       // Order number from BattleFace
  const [eventOrderNumber, setEventOrderNumber] = useState(null); // Event Ticket Protection order number
  const [confirmation, setConfirmation] = useState(null);     // Final confirmation response
  const [error, setError] = useState('');                     // Error messages
  const [paymentIntent, setPaymentIntent] = useState(null);   // Stripe payment result
  // Event Ticket Protection quote data
  const [eventPrice, setEventPrice] = useState(0);
  const [eventQuoteDetails, setEventQuoteDetails] = useState(null);
  // Store the Event Ticket Protection quote id for display on the Quote screen
  const [eventQuoteId, setEventQuoteId] = useState(null);
  // Toggle for Event Ticket Protection inclusion on the quote screen
  const [eventTicketIncluded, setEventTicketIncluded] = useState(true);
  // Destination distance from residence (>100 miles?)
  const [over100Miles, setOver100Miles] = useState(null);
  // Create Event Ticket Protection Quote using Event Details inputs
  const CreateEventTicketProtectionQuote = async () => {
    if (isMccMissing) {
      setError('Missing marketing channel (mcc). Cannot create event ticket protection quote.');
      return;
    }
    const ticketPriceNum = parseFloat(conferencePrice);
    if (!conferenceStart || !conferenceEnd || !conferencePrice || !singleTravelerAge) {
      setError('Please complete Event Details (dates, price, traveler age) before getting a quote.');
      return;
    }
    // Enforce Ticket Price maximum of $5,000
    if (isNaN(ticketPriceNum) || ticketPriceNum <= 0) {
      setError('Please enter a valid Event Ticket Price greater than $0.00.');
      return;
    }
    if (ticketPriceNum > 5000) {
      setError('Event Ticket Price cannot exceed $5,000. Please adjust the amount.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const startDate = conferenceStart instanceof Date
        ? conferenceStart.toISOString().split('T')[0]
        : new Date(conferenceStart).toISOString().split('T')[0];
      const endDate = conferenceEnd instanceof Date
        ? conferenceEnd.toISOString().split('T')[0]
        : new Date(conferenceEnd).toISOString().split('T')[0];

      const applicantsPayload = [{
        is_main: true,
        dob: calculateDateOfBirth(parseInt(singleTravelerAge)),
        trip_cost: ticketPriceNum
      }];

      const payload = {
        start_date: startDate,
        end_date: endDate,
        applicants: applicantsPayload,
        product: 1578,
        host_country: countryOfResidence,
        host_country_state: countryOfResidence === 'US' ? (homeCountryState || 'TX') : undefined,
        currency: 'USD',
        destinations: (destinationCountryCodes.length ? destinationCountryCodes : ['US']),
        packages: ['base_package']
        };

        console.log(JSON.stringify(payload));

      let res, data;
      res = await fetch('https://gateway.battleface.com/api/partner/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create event ticket protection quote');
      data = await res.json();

        console.log(data);

      // Store Event Ticket Protection details separately
      const details = Array.isArray(data?.data?.details) ? data.data.details : [];
      setEventQuoteDetails(details);
      // Save the event ticket protection quote id (prefer `id` over `quote_number`)
      const evtQuoteId = data.id || data.data?.id || data.quote_id || data.data?.quote_id || data.quote_number || data.data?.quote_number;
      if (evtQuoteId) setEventQuoteId(evtQuoteId);
      // Extract base package price for Event Ticket
      let evtAmt = 0;
      details.forEach(d => {
        const alias = d?.package_alias || d?.detail || d?.id || d?.product_package_id;
        if (!alias) return;
        const isBase = String(alias).toLowerCase().includes('base');
        let amount;
        if (typeof d?.amount !== 'undefined') amount = d.amount;
        else if (typeof d?.price !== 'undefined') amount = d.price;
        else if (typeof d?.total !== 'undefined') amount = d.total;
        if (isBase && typeof amount !== 'undefined') {
          const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
          if (!isNaN(num)) evtAmt = num;
        }
      });
      setEventPrice(evtAmt);
      // Do not change current quoteId or navigate; Travel Details will run full travel createQuote
      // Set quote so the Quote screen can render for Conference Only
      setQuote(data);

      await SaveQuote({
        endpoint: 'quote_event_details',
        marketing_channel_code: mcc,
          quote_id: evtQuoteId,
        session_id: sessionId,
        request: data,
        response: data,
        status: res?.status,
        timestamp: new Date().toISOString()
      }, evtQuoteId);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Optional packages configuration
  const BASE_ALIASES = [
    'trip_interruption',
    'trip_cancellation_package',
    'travel_medical_package',
    'baggage_package',
    'delay_package'
  ];
  const OPTIONAL_ALIASES = [
    'pet_travel_package',
    'rental_vehicle_damage',
    'vacation_rental_damage',
    'accidental_death_and_dismemberment'
  ];
  const OPTIONAL_LABELS = {
    rental_vehicle_damage: 'Rental Vehicle Damage',
    vacation_rental_damage: 'Vacation Rental Damage',
    accidental_death_and_dismemberment: 'Accidental Death & Dismemberment',
    pet_travel_package: 'Pet Travel Package'
  };

  const OPTIONAL_ICONS = {
    rental_vehicle_damage: <DirectionsCar sx={{ mr: 1 }} />,
    vacation_rental_damage: <HomeWork sx={{ mr: 1 }} />,
    accidental_death_and_dismemberment: <FlashOn sx={{ mr: 1 }} />,
    pet_travel_package: <Pets sx={{ mr: 1 }} />
  };

  // Icons for base benefit packages
  const BASE_ICONS = {
    event_ticket_protection: <ConfirmationNumber sx={{ mr: 1 }} />,
    trip_cancellation_package: <EventBusy sx={{ mr: 1 }} />,
    trip_interruption: <FlightTakeoff sx={{ mr: 1 }} />,
    delay_package: <AccessTime sx={{ mr: 1 }} />,
    baggage_package: <Luggage sx={{ mr: 1 }} />,
    travel_medical_package: <LocalHospital sx={{ mr: 1 }} />
  };

  const OPTIONAL_DESCRIPTIONS = {
    cancel_for_any_reason:
      'Cancellation of a covered trip for any reason not otherwise covered by the policy, up to 75% of nonrefundable trip costs. Must be purchased within 15 days of initial trip deposit, insure your full trip cost, and cancel before 48 hours of scheduled departure. Conditions apply. Available up to a maximum trip cost of $10,000 per traveling party.',
    rental_vehicle_damage:
      'Up to $35,000 for one rented vehicle per traveling party for theft or damages due to collision, vandalism, windstorm, fire, hail or flood, or any cause beyond your control while in your possession. $100 deductible.',
    vacation_rental_damage:
      'Coverage for accidental damage to a vacation rental property. Limits vary by selection.',
    accidental_death_and_dismemberment:
      'Benefit covers loss of eyesight or hearing, loss of limbs and death resulting from an accident while traveling. Coverage limit is chosen by you.',
    pet_travel_package:
      'Includes Pet Medical and Pet Return benefits for travel with pets.'
  };

  const [optionalSelections, setOptionalSelections] = useState(() => {
    // Default: all optional add-ons OFF (including CFAR)
    const initial = {};
    OPTIONAL_ALIASES.forEach(a => { initial[a] = false; });
    return initial;
  });

  // Base benefits are optional; default all ON
  const [baseSelections, setBaseSelections] = useState(() => {
    const initial = {};
    BASE_ALIASES.forEach(a => { initial[a] = true; });
    return initial;
  });

  // Optional add-on details are shown by default; no expand/collapse controls.

  // Per-option inputs
  const [cfarDepositDate, setCfarDepositDate] = useState(null);
  const [adndLimit, setAdndLimit] = useState(100000);
  const [vrdLimit, setVrdLimit] = useState(1500);
  const [vrdReservations, setVrdReservations] = useState(1);
  
  const resetOptionalSelections = () => {
    // Default reset: all optional add-ons OFF
    const initial = {};
    OPTIONAL_ALIASES.forEach(a => { initial[a] = false; });
    setOptionalSelections(initial);
    setCfarDepositDate(null);
  };

  // Whenever returning to Step 0 (starting quote over), clear optional add-ons
  useEffect(() => {
    if (activeStep === 0) {
      resetOptionalSelections();
      // Reset base selections to all ON when starting over
      const initialBase = {};
      BASE_ALIASES.forEach(a => { initialBase[a] = true; });
      setBaseSelections(initialBase);
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== 3 || isMccMissing || !tripCostCoverage) return;
    // CFAR: only requote when date is valid if selected (last 15 days from today)
    const fifteenDaysMs = 15 * 24 * 60 * 60 * 1000;
    const isCfarSelected = optionalSelections?.cancel_for_any_reason;
    const now = new Date();
    const hasValidCfarDate = !!cfarDepositDate &&
      cfarDepositDate <= now && cfarDepositDate >= new Date(now.getTime() - fifteenDaysMs);

    if (!isCfarSelected || hasValidCfarDate) {
      requoteWithNewCoverage(tripCostCoverage);
    }
  }, [adndLimit, vrdLimit, vrdReservations, optionalSelections, baseSelections, cfarDepositDate, tripStart, tripCostCoverage, activeStep, isMccMissing]);

  // When user indicates destination is NOT over 100 miles, force Event Ticket Protection to be included at $0.00
  useEffect(() => {
    if (over100Miles === false) {
      setEventTicketIncluded(true);
    }
  }, [over100Miles]);

  // CFAR not allowed: no special handling

  const currency = quote?.data?.currency_id || 'USD';

  // Extract amounts for known packages from quote details
  const detailAmounts = useMemo(() => {
    const arr = quote?.data?.details || [];
    const amounts = {};
    arr.forEach(d => {
      const alias = d?.package_alias || d?.detail || d?.id || d?.product_package_id;
      if (!alias) return;
      let amount;
      if (typeof d?.amount !== 'undefined') amount = d.amount;
      else if (typeof d?.price !== 'undefined') amount = d.price;
      else if (typeof d?.total !== 'undefined') amount = d.total;
      // Ignore benefit_limit values (not prices)
      if (typeof amount === 'undefined') return;
      const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
      if (!isNaN(num)) amounts[alias] = num;
    });
    return amounts;
  }, [quote]);

  // Amount per optional alias (best-effort alias matching)
  const optionalAmounts = useMemo(() => {
    const acc = {};
    OPTIONAL_ALIASES.forEach(alias => {
      const exact = detailAmounts[alias];
      if (typeof exact === 'number') {
        acc[alias] = exact;
      } else {
        const foundKey = Object.keys(detailAmounts).find(k => k.includes(alias));
        if (foundKey) acc[alias] = detailAmounts[foundKey];
      }
    });
    return acc;
  }, [detailAmounts]);

  // Cache optional add-on prices so they remain visible even if
  // a subsequent quote response omits non-selected items.
  const [optionalPriceCache, setOptionalPriceCache] = useState({});
  useEffect(() => {
    const next = { ...optionalPriceCache };
    OPTIONAL_ALIASES.forEach(alias => {
      const val = optionalAmounts[alias];
      if (typeof val === 'number') next[alias] = val;
    });
    setOptionalPriceCache(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalAmounts]);

  // Amount per base alias
  const baseAmounts = useMemo(() => {
    const acc = {};
    BASE_ALIASES.forEach(alias => {
      const exact = detailAmounts[alias];
      if (typeof exact === 'number') {
        acc[alias] = exact;
      } else {
        const foundKey = Object.keys(detailAmounts).find(k => k.includes(alias));
        if (foundKey) acc[alias] = detailAmounts[foundKey];
      }
    });
    return acc;
  }, [detailAmounts]);

  // Cache base package prices so they remain visible even if
  // a subsequent requote omits non-selected base items.
  const [basePriceCache, setBasePriceCache] = useState({});
  useEffect(() => {
    const next = { ...basePriceCache };
    BASE_ALIASES.forEach(alias => {
      const val = baseAmounts[alias];
      if (typeof val === 'number') next[alias] = val;
    });
    setBasePriceCache(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAmounts]);

  const displayTotal = useMemo(() => {
    let total = 0;
    // Conference Only: total should be the Event Ticket Protection price
    if (!insureAllTravel) {
      const evt = (typeof eventPrice === 'number' ? eventPrice : 0);
      const qp = quote?.data?.quoted_price;
      const qpNum = typeof qp === 'number' ? qp : Number(qp);
      const finalEvtTotal = evt > 0 ? evt : (!isNaN(qpNum) ? qpNum : 0);
      return { amount: finalEvtTotal, currency };
    }
    // Sum selected base packages
    BASE_ALIASES.forEach(alias => {
      if (baseSelections[alias]) {
        const amt = baseAmounts[alias];
        if (typeof amt === 'number') total += amt;
      }
    });
    // Sum selected optional add-ons
    OPTIONAL_ALIASES.forEach(alias => {
      if (optionalSelections[alias]) {
        const amt = optionalAmounts[alias];
        if (typeof amt === 'number') {
          total += alias === 'vacation_rental_damage' ? amt * (vrdReservations || 1) : amt;
        }
      }
    });
    // Add Event Ticket Protection price only when destination is over 100 miles
    if (eventTicketIncluded && over100Miles !== false && typeof eventPrice === 'number' && eventPrice > 0) {
      total += eventPrice;
    }
    // Fallback to quoted_price when detail parsing yields 0
    const qp = quote?.data?.quoted_price;
    const qpNum = typeof qp === 'number' ? qp : Number(qp);
    const finalTotal = total > 0 ? total : (!isNaN(qpNum) ? qpNum : 0);
    return { amount: finalTotal, currency };
  }, [insureAllTravel, baseSelections, baseAmounts, optionalSelections, optionalAmounts, vrdReservations, currency, eventPrice, eventTicketIncluded, quote]);

  const formatCurrency = (amt, curr) => `${curr || 'USD'} $${(Number(amt) || 0).toFixed(2)}`;

  // Right-side label renderer for cards: "Add to Plan" or "Included" (top), price below when available
  const renderPriceOrAddLabel = (checked, price) => {
    const headerText = checked ? 'Included' : 'Add to Plan';
    const hasPrice = typeof price === 'number';
    return (
      <Box textAlign="right" sx={{ lineHeight: 1.1 }}>
        <Typography variant="body2" fontWeight="bold">{headerText}</Typography>
        {hasPrice && (
          <Typography variant="body1" fontWeight="bold">${Number(price).toFixed(2)}</Typography>
        )}
      </Box>
    );
  };

  // Session tracking (per browser tab). Each new tab/window gets a fresh session id.
  // Logic:
  // 1. If a sid param is provided (?sid=...), use it (allows external correlation).
  // 2. Else, generate a new UUID and store in sessionStorage (tab-scoped).
  // 3. On errors or missing APIs, fallback to a timestamp/random based id.
  const [sessionId, setSessionId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sidParam = params.get('sid') || params.get('session') || params.get('sessionId');
      if (sidParam && sidParam.length > 5) {
        try { sessionStorage.setItem('bf_session_id', sidParam); } catch {}
        return sidParam;
      }
      // Always generate new per-tab (ignore any previous value). Use sessionStorage for potential reuse on reload.
      let newId;
      if (window.crypto?.randomUUID) {
        newId = window.crypto.randomUUID();
      } else {
        newId = 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }
      try { sessionStorage.setItem('bf_session_id', newId); } catch {}
      return newId;
    } catch {
      return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
  });

  // Email dialog state for sending quote/order reference to applicant
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailing, setEmailing] = useState(false);
  // Snackbar for professional feedback messages
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Marketing page toggle
  const [showMarketing, setShowMarketing] = useState(false);
  const [marketingHeight, setMarketingHeight] = useState(2500); // Increased default height to show all content

  const toggleMarketing = () => {
    setShowMarketing(prev => {
      const next = !prev;
      if (next) setShowTripMarketing(false); // ensure only one marketing view active
      return next;
    });
  };

  // Trip Protection marketing toggle
  const [showTripMarketing, setShowTripMarketing] = useState(false);
  const toggleTripMarketing = () => {
    setShowTripMarketing(prev => {
      const next = !prev;
      if (next) setShowMarketing(false); // ensure only one marketing view active
      return next;
    });
  };

  // Resolve marketing URL based on current theme
  const marketingUrl = useMemo(() => {
    const url = MARKETING_URLS[themeName] || MARKETING_URLS.default;
    console.log('[Marketing] Theme:', themeName, 'URL:', url);
    return url;
  }, [themeName]);

  // Dynamic height adjustment for marketing iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Listen for height messages from iframe content
      if (event.data && event.data.type === 'resize' && typeof event.data.height === 'number') {
        setMarketingHeight(event.data.height);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Marketing content is now loaded dynamically via iframe based on theme
  // See MARKETING_URLS configuration at top of file

  const handleOpenEmailDialog = () => setOpenEmailDialog(true);
  const handleCloseEmailDialog = () => {
    if (!emailing) setOpenEmailDialog(false);
  };

  const handleConfirmEmail = async () => {
    setEmailing(true);
    const mainApplicant = applicants.find(a => a.is_main) || applicants[0];
    const recipient = mainApplicant?.email?.trim();
    if (!recipient) {
      showSnackbar('No applicant email provided. Please enter an email address on the applicant form.', 'warning');
      setEmailing(false);
      return;
    }
    try {
      // Compose email content (HTML + plain text fallback)
      const applicantName = [mainApplicant.first_name, mainApplicant.last_name].filter(Boolean).join(' ') || 'Customer';
      const orderRef = orderNumber || 'N/A';
      const subject = `Event Ticket & Trip Protection Quote - Reference: ${orderRef}`;


    // Use the computed display total for consistency with the UI
    const planCostDisplay = `${displayTotal.currency || 'USD'} ${(Number(displayTotal.amount) || 0).toFixed(2)}`;
    const tripCost = Number(tripCostCoverage || 0);
    const currencyCode = displayTotal.currency || 'USD';
    const coverageLimitDisplay = `${currencyCode} $${tripCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const interruptionAmt = Math.round(tripCost * 1.25 * 100) / 100; // round to nearest penny
    const tripInterruptionDisplay = `${currencyCode} $${interruptionAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Itinerary text (keeps same format area, just more relevant details)
    const startText = tripStart ? tripStart.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : null;
    const endText = tripEnd ? tripEnd.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : null;
    const itineraryText = startText && endText
      ? `Trip Dates: ${startText} – ${endText}`
      : startText
        ? `Trip Start: ${startText}`
        : undefined;

    // Build selected add-ons list only for opted-in items
    const selectedAddOns = OPTIONAL_ALIASES
      .filter(alias => !!optionalSelections[alias])
      .map(alias => {
        const label = OPTIONAL_LABELS[alias] || alias;
        let price = optionalAmounts[alias];
        if (alias === 'vacation_rental_damage' && typeof price === 'number') {
          price = price * (vrdReservations || 1);
        }
        const priceDisplay = typeof price === 'number' ? `${displayTotal.currency || 'USD'} $${Number(price).toFixed(2)}` : undefined;
        let limitDisplay;
        let description = OPTIONAL_DESCRIPTIONS[alias];
        let detailsHtml;
        switch (alias) {
          case 'accidental_death_and_dismemberment':
            limitDisplay = `$${Number(adndLimit).toLocaleString()}`;
            break;
          case 'vacation_rental_damage':
            limitDisplay = `$${Number(vrdLimit).toLocaleString()}`;
            break;
          case 'rental_vehicle_damage':
            limitDisplay = '$35,000';
            break;
          case 'cancel_for_any_reason':
            limitDisplay = 'Up to 75% of trip cost';
            break;
          case 'pet_travel_package':
            // Include Pet Medical & Pet Return details per screenshot
            limitDisplay = undefined;
            detailsHtml = `
              <div style="font-size:13px;color:#333333;">
                <div style="font-weight:bold; margin-bottom:6px;">Includes Pet Medical &amp; Pet Return</div>
                <ul style="margin:0 0 0 18px; padding:0;">
                  <li>Pet Return — $500</li>
                  <li>Pet Medical Expense — $2,000 • Deductible $100</li>
                </ul>
              </div>`;
            break;
          default:
            limitDisplay = undefined;
        }
        return { label, priceDisplay, limitDisplay, description, detailsHtml };
      });

    const htmlBody = buildPlanEmail({
      applicantName,
      orderRef,
      planCostDisplay,
      coverageLimitDisplay,
      tripInterruptionDisplay,
      marketingChannelCode: mcc || '',
      itineraryText,
      numTravelers,
      selectedAddOns,
      currency: displayTotal.currency || 'USD'
    });

      // POST to Azure Function endpoint        
        const response = await fetch(`${backendBase}/Data/SendEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: recipient, subject: subject, body: htmlBody })
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        const msg = `Failed to send email (status ${response.status}). ${bodyText || ''}`.trim();
        console.error(msg);
        showSnackbar(msg, 'error');
      } else {
        showSnackbar('Quote email queued for delivery.', 'success');
      }
    } catch (err) {
      console.error('Email send error:', err);
      showSnackbar(`Error sending email: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setEmailing(false);
      setOpenEmailDialog(false);
    }
  };

  // Applicant information states - stores personal details for all travelers
  const [applicants, setApplicants] = useState([{
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    address: '',
    city: '',
    post_code: '',
    country_code: countryOfResidence,
    state_code: '',
    is_main: true  // First applicant is always the main applicant
  }]);

  // Effect: Update all applicants' state when homeCountryState changes
  // This ensures consistency across all travelers' addresses
  useEffect(() => {
    if (homeCountryState) {
      setApplicants(prev => prev.map(applicant => ({
        ...applicant,
        state_code: homeCountryState
      })));
    }
  }, [homeCountryState]);

  // Effect: Update all applicants' country when countryOfResidence changes
  // If non-US is selected, clear homeCountryState and applicants' state_code
  useEffect(() => {
    setApplicants(prev => prev.map(applicant => ({
      ...applicant,
      country_code: countryOfResidence,
      state_code: countryOfResidence === 'US' ? applicant.state_code : ''
    })));
    if (countryOfResidence !== 'US') {
      setHomeCountryState('');
    }
  }, [countryOfResidence]);

  // Set initial trip start date to first allowable date
  useEffect(() => {
    if (!tripStart) {
      const firstAllowableDate = findFirstAllowableDate();
      if (firstAllowableDate) {
        setTripStart(firstAllowableDate);
      }
    }
  }, [tripStart]);

  // Validation
  const errors = {};
  // Only enforce trip-specific validations when user insures travel
  if (insureAllTravel) {
    if (!tripStart) errors.tripStart = 'Plan start date required';
    if (!tripEnd) errors.tripEnd = 'Trip end date required';
    if (tripStart && tripEnd && tripEnd <= tripStart) errors.tripEnd = 'End date must be after start date';
  }
  if (countryOfResidence === 'US' && !homeCountryState) errors.homeCountryState = 'Home state required';
  // Trip Amount required and must be positive and <= $20,000 per traveling party
  if (insureAllTravel) {
    const tripAmt = parseFloat(tripCostCoverage);
    if (tripCostCoverage === '' || isNaN(tripAmt) || tripAmt <= 0) {
      errors.tripAmount = 'Trip amount required';
    } else if (tripAmt > 20000) {
      errors.tripAmount = 'The maximum insurable trip cost is $20,000 per traveling party. Please update your entry.';
    }
  }
  // CFAR not allowed: no validation
  if (numTravelers < 1 || numTravelers > 10) errors.numTravelers = '1-10 travelers allowed';
  travelerAges.forEach((age, idx) => {
    if (age !== '' && (isNaN(age) || parseInt(age) < 0 || parseInt(age) > 120)) {
      errors[`age${idx}`] = 'Enter valid age (0-120)';
    } else if (age === '') {
      errors[`age${idx}`] = 'Age required';
    }
  });

  // Require destination selection and 100-miles question on Event Details/Travel Details
  if (activeStep === 1) {
    // Require Event Name and Event City
    if (!eventName || !eventName.trim()) {
      errors.eventName = 'Event name required';
    }
    if (!eventCity || !eventCity.trim()) {
      errors.eventCity = 'Event city required';
    }
    // Require registration type selection
    if (!attendeeRole) {
      errors.attendeeRole = 'Please select registration type';
    }
    if (over100Miles === null) {
      errors.over100Miles = 'Please indicate if the destination is more than 100 miles from your residence';
    }
    // Ticket start date must be at least 1 day in the future
    if (conferenceStart) {
      const today = new Date();
      today.setHours(0,0,0,0);
      const minStart = new Date(today);
      minStart.setDate(minStart.getDate() + 1);
      const start = new Date(conferenceStart);
      start.setHours(0,0,0,0);
      if (start < minStart) {
        errors.conferenceStart = 'Start date must be at least 1 day from today';
      }
    } else {
      errors.conferenceStart = 'Ticket start date required';
    }
    // End date can be the same as start date or any date in the future
    if (conferenceEnd) {
      if (conferenceStart) {
        const start = new Date(conferenceStart);
        start.setHours(0,0,0,0);
        const end = new Date(conferenceEnd);
        end.setHours(0,0,0,0);
        if (end < start) {
          errors.conferenceEnd = 'End date cannot be before start date';
        }
      }
    } else {
      errors.conferenceEnd = 'Ticket end date required';
    }
    // Ticket purchase date must be before the ticket start date
    if (ticketPurchaseDate) {
      if (conferenceStart) {
        const start = new Date(conferenceStart);
        start.setHours(0,0,0,0);
        const purchase = new Date(ticketPurchaseDate);
        purchase.setHours(0,0,0,0);
        if (!(purchase < start)) {
          errors.ticketPurchaseDate = 'Purchase date must be before the start date';
        }
      }
    } else {
      errors.ticketPurchaseDate = 'Ticket purchase date required';
    }
    // Ticket Price must be numeric and <= $5,000
    const priceNum = parseFloat(conferencePrice);
    if (conferencePrice === '' || isNaN(priceNum) || priceNum <= 0) {
      errors.conferencePrice = 'Enter a valid Event Ticket Price greater than $0.00';
    } else if (priceNum > 5000) {
      errors.conferencePrice = 'Event Ticket Price cannot exceed $5,000';
    }
  }
  if (activeStep === 2) {
    const hasDestinations = Array.isArray(destinationCountryCodes) && destinationCountryCodes.length > 0;
    if (!hasDestinations) {
      errors.destinations = 'Please select at least one destination';
    }
  }

  // Validate applicant information for step 2 (Order step)
  if (activeStep === 4) {
    applicants.forEach((applicant, idx) => {
      if (!applicant.first_name) errors[`applicant${idx}_first_name`] = 'First name required';
      if (!applicant.last_name) errors[`applicant${idx}_last_name`] = 'Last name required';
      
      // Enhanced email validation using helper function
      const emailValidation = validateEmail(applicant.email);
      if (!emailValidation.isValid) {
        errors[`applicant${idx}_email`] = emailValidation.message;
      }
      
      if (!applicant.post_code) errors[`applicant${idx}_post_code`] = 'Postal code required';
      if (!applicant.contact_number) {
        errors[`applicant${idx}_contact_number`] = 'Phone number required';
      } else {
        // Remove all non-digits to validate
        const digitsOnly = applicant.contact_number.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
          errors[`applicant${idx}_contact_number`] = 'Phone number must be 10 digits';
        }
      }
    });
  }

  // Handlers
  const handleNumTravelersChange = (e) => {
    const n = parseInt(e.target.value, 10);
    setNumTravelers(n);
    setTravelerAges((prev) => {
      const arr = [...prev];
      while (arr.length < n) arr.push('');
      while (arr.length > n) arr.pop();
      return arr;
    });
    
    // Update applicants array to match number of travelers
    setApplicants((prev) => {
      const arr = [...prev];
      while (arr.length < n) {
        arr.push({
          first_name: '',
          middle_name: '',
          last_name: '',
          email: '',
          contact_number: '',
          address: '',
          city: '',
          post_code: '',
          country_code: countryOfResidence,
          state_code: countryOfResidence === 'US' ? homeCountryState : '',
          is_main: arr.length === 0 // First applicant is main
        });
      }
      while (arr.length > n) arr.pop();
      return arr;
    });
  };

  const handleAgeChange = (idx, value) => {
    setTravelerAges((prev) => {
      const arr = [...prev];
      arr[idx] = value;
      return arr;
    });
  };

  const handleApplicantChange = (idx, field, value) => {
    setApplicants((prev) => {
      const arr = [...prev];
      
      // Format phone number if it's the contact_number field
      if (field === 'contact_number') {
        value = formatPhoneNumber(value);
      }
      
      // Trim whitespace for email field
      if (field === 'email') {
        value = value.trim().toLowerCase();
      }
      
      arr[idx] = { ...arr[idx], [field]: value };
      return arr;
    });
  };

  // API calls - These functions handle communication with BattleFace API
  
  /**
   * STEP 0 → STEP 1: Create Initial Quote
   * Purpose: Get pricing from BattleFace based on trip details and traveler ages
   * API: POST /api/partner/quote
  // API Functions - These handle communication with BattleFace API

  /**
   * STEP 1: Create Initial Quote
   * Called when user submits the Details form (step 0)
   * Sends trip information to BattleFace API to get pricing quote
   * Input: Trip dates, traveler ages, coverage amount, location
   * Output: Quote with pricing and quote ID for next steps
   */

    async function AuthorizeMarketingChannel(marketingChannelCode) {
    try {      
        const productCode = 'BattleFace Event Ticket & Trip Protection';
        const url = `${backendBase}/Data/authenticate?marketingChannelCode=${encodeURIComponent(marketingChannelCode)}&productCode=${encodeURIComponent(productCode)}`;      
      
      const res = await fetch(url);
              if (res.ok) {
                const json = await res.json();
                                                
                if (json) {                             
                
                  return json;
                }
              }
            } catch (err) {
              // Optionally handle error
              console.error('Failed to fetch authorization from URL', err);
              return "";
            }
    }    

    async function SaveOrder(logData, orderId) {
      try {
        
        //console.log("Saving order log data", JSON.stringify(logData));
        
          await fetch(`${backendBase}/Data/SaveOrder?orderId=${encodeURIComponent(orderId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveOrder', err);
      }
    }

    async function SaveQuote(logData, quoteId) {
      try {          

        //console.log("Saving quote log data", JSON.stringify(logData));

          await fetch(`${backendBase}/Data/SaveQuote?quoteId=${encodeURIComponent(quoteId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveQuote', err);
      }
    }

    /**
     * Log Battleface API errors to the backend for troubleshooting.
     * This function is called when any Battleface API call fails.
     * @param {Object} errorData - Error details to log
     * @param {string} errorData.endpoint - The Battleface API endpoint that was called
     * @param {string} errorData.httpMethod - HTTP method used (GET, POST, etc.)
     * @param {number} errorData.httpStatusCode - HTTP status code returned (0 for network errors)
     * @param {string} errorData.errorMessage - The error message or description
     * @param {string} errorData.requestPayload - The request payload sent (JSON string)
     * @param {string} errorData.responsePayload - The response body received (JSON string)
     * @param {string} errorData.additionalContext - Any additional context
     */
    async function LogBattlefaceError(errorData) {
      try {
        const payload = {
          sessionId: sessionId || null,
          marketingChannelCode: mcc || null,
          endpoint: errorData.endpoint,
          httpMethod: errorData.httpMethod || 'POST',
          httpStatusCode: errorData.httpStatusCode || 0,
          errorMessage: errorData.errorMessage,
          requestPayload: errorData.requestPayload || null,
          responsePayload: errorData.responsePayload || null,
          widgetName: 'EventTicketProtection',
          userAgent: navigator.userAgent,
          additionalContext: errorData.additionalContext || null
        };

        await fetch(`${backendBase}/Data/LogError`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (err) {
        // Don't block user if error logging fails - just log to console
        console.error('Failed to log Battleface error:', err);
      }
    }

    async function PaymentConfirmed(logData, orderId) {
      try {
              

          await fetch(`${backendBase}/Data/PaymentConfirmed?orderId=${encodeURIComponent(orderId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveOrder', err);
      }
    }    

    async function RetrieveOrder(orderid) {
    try {
      
        const url = `${backendBase}/Data/GetOrder?orderId=${encodeURIComponent(orderid)}`;
      
      const res = await fetch(url);
              if (res.ok) {
                const json = await res.json();
                if (json && json.response) {

                  var originalResponse = json.response;    
                  setSessionId(json.session_id);   
                
                  return originalResponse.data;
                }
              }
            } catch (err) {
              // Optionally handle error
              console.error('Failed to fetch quote by quoteId from URL', err);
              return "";
            }
    }
    
    async function RetrieveQuote(quoteId) {
    try {
      // Use local backend (WidgetBackend) DataController to retrieve quote by ID            
      const url = `${backendBase}/Data/GetQuote?quoteId=${encodeURIComponent(quoteId)}`;
                  
      const res = await fetch(url);
              if (res.ok) {
                  const json = await res.json();                  

                // Local backend returns raw JSON document content for the quote
                  if (json) {

                      //console.log(json);
                  return json;
                }
              }
            } catch (err) {
              // Optionally handle error
              console.error('Failed to fetch quote by quoteId from URL', err);
              return "";
            }
    }        


  const createQuote = async () => {
    // Do not run travel quote when user selected Conference Only
    if (!insureAllTravel) {
      return;
    }
    if (isMccMissing) {
      setError('Missing marketing channel (mcc). Cannot create quote.');
      return;
    }
    setLoading(true); 
    setError('');
    
    try {
      // Build the payload according to BattleFace API requirements
      const payload = {
        "start_date": tripStart ? tripStart.toISOString().split('T')[0] : "2025-12-31",
        "end_date": tripEnd ? tripEnd.toISOString().split('T')[0] : "2026-01-02",     
        "applicants": travelerAges.map((age, index) => ({            
            "is_main": index === 0,                                    // First traveler is main applicant
            "dob": calculateDateOfBirth(parseInt(age)),                // Convert age to date of birth
          "trip_cost": parseFloat(tripCostCoverage)                  // Trip amount entered by user
        })),
        "product": 1042,                                               // BattleFace product ID (could be configurable)
        "host_country": countryOfResidence,                             // Country of residence
        "host_country_state": countryOfResidence === 'US' ? (homeCountryState || "TX") : undefined,               // State only when US
        "currency": "USD",                                             // Always USD for this implementation
        "destinations": destinationCountryCodes.length ? destinationCountryCodes : ["US"],        // Trip destinations (selected or default US)
        "packages": (() => {
          // Build selected base packages (all benefits optional)
          const pkgs = [];
          BASE_ALIASES.forEach(id => { if (baseSelections[id]) pkgs.push(id); });
          // Always include optional packages in the initial quote to retrieve pricing
          pkgs.push({
            id: "accidental_death_and_dismemberment",
            limits: [{ type: "benefit_limit", for: "non_flight_accidental_death_and_dismemberment", value: String(adndLimit) }]
          });
          pkgs.push({
            id: "vacation_rental_damage",
            limits: [{ type: "benefit_limit", for: "vacation_rental_damage", value: String(vrdLimit) }],
            reservations: [
                {
                    "type": "reservations",
                    "for": "vacation_rental_damage",
                    "amount": 1
                }
            ]

          }
        );
          pkgs.push("rental_vehicle_damage");
          pkgs.push("pet_travel_package");
          // CFAR not allowed
          return pkgs;
        })()
      };

      //console.log('Quote payload:', JSON.stringify(payload));

      let res, data, extractedQuoteId;

      // Send quote request to BattleFace API
      try {
          res = await fetch('https://gateway.battleface.com/api/partner/quote', {
            method: 'POST',
              headers: { 
                'Content-Type': 'application/json', 
                Authorization: BATTLEFACE_API_TOKEN 
              },
            body: JSON.stringify(payload),
          });          

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/quote',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to create quote: HTTP ${res.status}`,
          requestPayload: JSON.stringify(payload),
          responsePayload: responseText,
          additionalContext: 'createQuote function'
        });
        throw new Error('Failed to create quote');
      }
          data = await res.json();

      setQuote(data);      

      // Extract quote ID from response (API may return it in different locations)
      extractedQuoteId = data.quote_number || data.id || data.quote_id || 
                               data.data?.id || data.data?.quote_id || data.data?.quote_number;

      if (!extractedQuoteId) {
        throw new Error('No quote ID found in API response');
      }

      setQuoteId(extractedQuoteId); 
  guardedSetActiveStep(3); // Move to Quote Review step (guarded)

          // ...existing code...
        } finally {

      await SaveQuote({
        endpoint: 'quote',
        marketing_channel_code: mcc,
        quote_id: extractedQuoteId,
        session_id: sessionId,
        request: extractedQuoteId,
        response: data,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedQuoteId);          

      }      

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * STEP 1: Update Quote with New Coverage Amount
   * Purpose: Recalculate pricing when user changes trip cancellation coverage
   * API: POST /api/partner/quote (same as initial quote but with new coverage)
   * Input: Same as createQuote but with updated coverage amount
   * Output: Updated quote with new pricing
   */
  /**
   * STEP 1 VARIANT: Requote with Different Coverage Amount
   * Called when user changes coverage amount in the dropdown on step 1
   * Gets updated pricing for the new coverage level
   * Input: New coverage amount (2500, 5000, 10000, 20000, or 30000)
   * Output: Updated quote with new pricing
   */
  const requoteWithNewCoverage = async (newCoverageAmount) => {
    if (isMccMissing) {
      setError('Missing marketing channel (mcc). Cannot requote.');
      return;
    }
    if (typeof newCoverageAmount !== 'undefined' && newCoverageAmount !== null) {
      setTripCostCoverage(newCoverageAmount);
    }
    setLoading(true); 
    setError('');
    
    try {
      // Build payload with updated coverage amount - same structure as initial quote
      const formatYmd = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : new Date(d).toISOString().split('T')[0]);
      const todayYmd = new Date().toISOString().split('T')[0];
      const packages = (() => {
        // Build selected base packages (all benefits optional)
        const pkgs = [];
        BASE_ALIASES.forEach(id => { if (baseSelections[id]) pkgs.push(id); });
        // Include optional packages only if selected
        if (optionalSelections?.accidental_death_and_dismemberment) {
          pkgs.push({
            id: "accidental_death_and_dismemberment",
            limits: [{ type: "benefit_limit", for: "non_flight_accidental_death_and_dismemberment", value: String(adndLimit) }]
          });
        }
        if (optionalSelections?.vacation_rental_damage) {
          pkgs.push({
            id: "vacation_rental_damage",
            limits: [{ type: "benefit_limit", for: "vacation_rental_damage", value: String(vrdLimit) }],
            reservations: [
                {
                    "type": "reservations",
                    "for": "vacation_rental_damage",
                    "amount": Number(vrdReservations || 1)
                }
            ]            
          });
        }
        if (optionalSelections?.rental_vehicle_damage) pkgs.push("rental_vehicle_damage");
        if (optionalSelections?.pet_travel_package) pkgs.push("pet_travel_package");
        // CFAR not allowed
        return pkgs;
      })();
      // Maintain ordering by leaving CFAR where added above

      const payload = {
        start_date: tripStart ? tripStart.toISOString().split('T')[0] : "2025-12-31",
        end_date: tripEnd ? tripEnd.toISOString().split('T')[0] : "2026-01-02",
        // CFAR not allowed: do not send deposit_date
        applicants: travelerAges.map((age, index) => ({            
            is_main: index === 0,
            dob: calculateDateOfBirth(parseInt(age)),
            trip_cost: parseFloat(newCoverageAmount ?? tripCostCoverage)
        })),
        product: 1042,
        host_country: countryOfResidence,
        host_country_state: countryOfResidence === 'US' ? (homeCountryState || "TX") : undefined,
        currency: "USD",
        destinations: destinationCountryCodes.length ? destinationCountryCodes : ["US"],
        packages
      };

      //console.log(JSON.stringify(payload));

      // Send requote request to BattleFace API (same endpoint as initial quote)
      const res = await fetch('https://gateway.battleface.com/api/partner/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/quote',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to get new quote: HTTP ${res.status}`,
          requestPayload: JSON.stringify(payload),
          responsePayload: responseText,
          additionalContext: 'requoteWithNewCoverage function'
        });
        throw new Error('Failed to get new quote');
      }
      const data = await res.json();

      setQuote(data);

      //console.log("Data:" +JSON.stringify(data));

      // Update quote ID if it changed (may be same or different)
      const extractedQuoteId = data.quote_number || data.id || data.quote_id || 
                               data.data?.id || data.data?.quote_id || data.data?.quote_number;

      if (extractedQuoteId) {
        setQuoteId(extractedQuoteId);
      }      

      await SaveQuote({
        endpoint: 'quote',
        quote_id: extractedQuoteId,
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedQuoteId,
        response: data,
        status: res?.status,        
        timestamp: new Date().toISOString(),
      }, extractedQuoteId);     


    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * STEP 4: Confirm Quote/Policy After Payment
   * Called after successful payment to finalize the insurance policy
   * Currently uses GET request but may need adjustment based on API docs
   * Input: Quote ID from initial quote and applicant details collected in step 2
   * Output: Policy confirmation with policy number
   * Note: This is called automatically after successful payment
   */
  const confirmQuote = async (passedQuoteId, passedOrderNumber) => {
    const idToUse = passedQuoteId || quoteId;
    const orderToUse = passedOrderNumber || orderNumber;
    if (!idToUse) {
      setError('No quote ID available. Please get a quote first.');
      return;
    }
    if (!orderToUse) {
      setError('No order number available. Please create an order first.');
      return;
    }
    let data, res;
    setLoading(true); setError('');
    try {
      // POST/PUT as per API requirements
      res = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderToUse}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN }
      });
      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: `https://gateway.battleface.com/api/partner/orders/${orderToUse}`,
          httpMethod: 'GET',
          httpStatusCode: res.status,
          errorMessage: `Failed to confirm quote: HTTP ${res.status}`,
          requestPayload: null,
          responsePayload: responseText,
          additionalContext: 'confirmQuote function'
        });
        throw new Error('Failed to confirm quote');
      }
      data = await res.json();

      console.log(JSON.stringify(data));

      setConfirmation(data);

      // Extract and store the order number
      const extractedOrderNumber = data.order_number || data.orderNumber || data.id || data.data?.order_number || data.data?.id;
      

      await PaymentConfirmed({
        endpoint: 'payment',
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedOrderNumber,
        response: data,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedOrderNumber);         



      setActiveStep(6);
    } catch (e) {
      setError(e.message);
    } finally {
      // Log the API call regardless of success/failure

      setLoading(false);
    }
  };

  /**
   * STEP 1 → STEP 2: Move from Quote Review to Order Details
   * Purpose: Allow user to proceed to providing applicant information
   * No API call - just UI navigation
   */
  const proceedToOrder = () => {
    // Move from quote review (step 3) to order (step 4)
    setActiveStep(4);
  };

  /**
   * STEP 2 → STEP 3: Create Order with BattleFace
  /**
   * STEP 2 → STEP 3: Create Order with Applicant Details
   * Called when user completes the Order Information form (step 2)
   * Creates a formal order with all applicant details before payment
   * API: POST /api/partner/orders/condensed
   * Input: Quote ID, applicant details, coverage selections
   * Output: Order number for payment processing
   */
  const processOrder = async () => {
    // Branching: handle Event Ticket Protection vs Travel Protection
    // Conference Only: only place Event Ticket Protection order
    if (!insureAllTravel) {
      await processEventTicketProtectionOrder();
      return;
    }

    // Validate that we have a travel quote ID before creating travel order
    if (!quoteId) {
      setError('No travel quote ID available. Please get a quote first.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // If Event Ticket Protection is included for Ticket Plus Travel, create its order first
      if (eventTicketIncluded) {
        await processEventTicketProtectionOrder();
      }
      // Build order payload with all collected information
      const formatYmd = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : new Date(d).toISOString().split('T')[0]);
      // Base packages included conditionally based on user toggles
      const baseDetails = BASE_ALIASES
        .filter(id => !!baseSelections[id])
        .map(id => ({ type: "package", product_package_id: id }));
      const details = [...baseDetails];
      // Conditionally include optional packages selected by the user
      if (optionalSelections?.accidental_death_and_dismemberment) {
        details.push(
          {
            "product_package_id": "accidental_death_and_dismemberment",
            "type": "package"
          },
          {
          product_package_id: "accidental_death_and_dismemberment",
          type: "limit",
          detail: "benefit_limit",
          for: "non_flight_accidental_death_and_dismemberment",
          value: String(adndLimit),          
        });
      }
      if (optionalSelections?.vacation_rental_damage) {
        details.push(
          {
            "product_package_id": "vacation_rental_damage",
            "type": "package"
          },
            {
              "product_package_id": "vacation_rental_damage",
              "type": "reservations",
              "amount": vrdReservations
            },
          {          
          product_package_id: "vacation_rental_damage",
          type: "limit",
          detail: "benefit_limit",
          for: "vacation_rental_damage",
          value: String(vrdLimit)
        });
      }
      if (optionalSelections?.rental_vehicle_damage) {
        details.push({ type: "package", product_package_id: "rental_vehicle_damage" });
      }
      if (optionalSelections?.pet_travel_package) {
        details.push({ type: "package", product_package_id: "pet_travel_package" });
      }
      // CFAR not allowed: do not add package

      const orderPayload = {
        quote_id: quoteId,                                             // Quote ID from step 1
        currency_id: "USD",
        product_id: 1042,
        start_at: tripStart ? tripStart.toISOString().split('T')[0] : "2025-06-29",
        end_at: tripEnd ? tripEnd.toISOString().split('T')[0] : "2025-07-04",
        deposit_at: new Date().toISOString().split('T')[0],           // Today's date
        // CFAR not allowed: no deposit_date
        details,
        applicants: applicants.map((applicant, index) =>              // All traveler details
          cleanApplicantData(applicant, index, travelerAges, parseFloat(tripCostCoverage)) // Remove empty middle_name
        ),
        destinations: (destinationCountryCodes.length
          ? destinationCountryCodes.map(code => ({ country_code: code }))
          : [{ country_code: "US" }])
      };

      //console.log(JSON.stringify(orderPayload));

      // Send order creation request to BattleFace API
      const res = await fetch('https://gateway.battleface.com/api/partner/orders/condensed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(orderPayload)
      });

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/orders/condensed',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to create order: HTTP ${res.status}`,
          requestPayload: JSON.stringify(orderPayload),
          responsePayload: responseText,
          additionalContext: 'processOrder function'
        });
        throw new Error('Failed to create order');
      }

      const orderData = await res.json();

      


      // Extract and store the order number
      const extractedOrderNumber = orderData.order_number || orderData.orderNumber || orderData.id || orderData.data?.order_number || orderData.data?.id;
      //console.log('Extracted Order Number:', extractedOrderNumber);

      await SaveOrder({
        endpoint: 'order',
        marketing_channel_code: mcc,
        quote_id: extractedOrderNumber,        
        session_id: sessionId,
        request: extractedOrderNumber,
        response: orderData,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedOrderNumber);      
      
      if (!extractedOrderNumber) {
        throw new Error('No order number found in API response');
      }

      setOrderNumber(extractedOrderNumber);
      
      // Move to payment step (step 5)
      setActiveStep(5);
      
    } catch (e) {
      console.error('Create Order Error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Creates an order for Event Ticket Protection using the event quote data
  const processEventTicketProtectionOrder = async () => {
    // Validate that we have an Event Ticket Protection quote ID before creating order
    if (!eventQuoteId) {
      setError('No event ticket quote ID available. Please get a quote first.');
      return;
    }

    // Ensure ticket price is valid and within $5,000 max
    const ticketPriceNum = parseFloat(conferencePrice);
    if (isNaN(ticketPriceNum) || ticketPriceNum <= 0) {
      setError('Please enter a valid Event Ticket Price greater than $0.00.');
      return;
    }
    if (ticketPriceNum > 5000) {
      setError('Event Ticket Price cannot exceed $5,000. Please adjust the amount.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formatYmd = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : new Date(d).toISOString().split('T')[0]);

      // For Event Ticket Protection, build minimal details using base package from the quote
      // If specific details are needed, they can be derived from eventQuoteDetails
      const details = [
        { type: 'package', product_package_id: 'base_package' }
      ];

      // Build full applicant payload for Event Ticket Protection using main applicant details
      const mainApplicant = applicants?.find(a => a.is_main) || applicants?.[0] || {};
      const eventApplicant = {
        first_name: mainApplicant.first_name || '',
        last_name: mainApplicant.last_name || '',
        dob: calculateDateOfBirth(parseInt(singleTravelerAge)),
        email: mainApplicant.email || '',
        contact_number: (mainApplicant.contact_number || '').replace(/\D/g, ''),
        address: mainApplicant.address || '',
        city: mainApplicant.city || '',
        post_code: mainApplicant.post_code || '',
        country_code: mainApplicant.country_code || countryOfResidence || 'US',
        state_code: mainApplicant.state_code || homeCountryState || '',
        is_main: true,
        trip_cost: ticketPriceNum
      };
      if (mainApplicant.middle_name && mainApplicant.middle_name.trim() !== '') {
        eventApplicant.middle_name = mainApplicant.middle_name;
      }
      const eventApplicants = [eventApplicant];

      const orderPayload = {
        quote_id: eventQuoteId,
        currency_id: 'USD',
        product_id: 1578,
        start_at: conferenceStart ? formatYmd(conferenceStart) : formatYmd(new Date()),
        end_at: conferenceEnd ? formatYmd(conferenceEnd) : formatYmd(new Date()),
        deposit_at: formatYmd(ticketPurchaseDate || new Date()),
        details,
        applicants: eventApplicants,
        destinations: (destinationCountryCodes.length
          ? destinationCountryCodes.map(code => ({ country_code: code }))
          : [{ country_code: 'US' }])
        };

        console.log(JSON.stringify(orderPayload));

      const res = await fetch('https://gateway.battleface.com/api/partner/orders/condensed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(orderPayload)
      });        

      if (!res.ok) {
        throw new Error('Failed to create event ticket protection order');
      }

      const orderData = await res.json();

        console.log(orderData)

      const extractedOrderNumber = orderData.order_number || orderData.orderNumber || orderData.id || orderData.data?.order_number || orderData.data?.id;

      await SaveOrder({
        endpoint: 'order_event_ticket',
        marketing_channel_code: mcc,
        quote_id: extractedOrderNumber,
        session_id: sessionId,
        request: extractedOrderNumber,
        response: orderData,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedOrderNumber);

      if (!extractedOrderNumber) {
        throw new Error('No event order number found in API response');
      }

      setEventOrderNumber(extractedOrderNumber);

      // If user selected Conference Only, set the payment context to this order and move to payment step
      if (!insureAllTravel) {
        setOrderNumber(extractedOrderNumber);
        setActiveStep(5);
      }
    } catch (e) {
      console.error('Create Event Ticket Order Error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const processPayment = () => {
    // Move from payment (step 3) to confirmation (step 4)
    // Process payment and confirm quote
    confirmQuote();
  };

  /**
  // Payment Handler Functions

  /**
   * STEP 3 → STEP 4: Handle Successful Payment
   * Called by PaymentForm component after Stripe payment succeeds
   * Actions: Store payment details, call confirmQuote to finalize policy
   * This triggers the final step transition to confirmation/download
   */
  const handlePaymentSuccess = async (paymentIntentResult) => {
    setPaymentIntent(paymentIntentResult);
    
    // After successful payment, confirm the quote to create the final policy
    try {
      await confirmQuote(); // This will move to step 6 (Download/Confirmation)
    } catch (error) {
      setError('Payment succeeded but policy confirmation failed. Please contact support.');
    }
  };

  /**
   * Handle Payment Errors
   * Called by PaymentForm component when payment fails
   * Displays user-friendly error message
   */
  const handlePaymentError = (errorMessage) => {
    setError(`Payment failed: ${errorMessage}`);
  };

  // Responsive: narrow screens (<=390px)
  const isNarrow = useMediaQuery('(max-width:390px)');
  // Celebration effects only on download/confirmation screen
  const showCelebration = activeStep === (steps.length - 1) && !!confirmation;

  // UI Rendering - The main component render with 5 steps
  return (
  <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box maxWidth={650} mx="auto" boxShadow={3} borderRadius={3} bgcolor="background.paper" overflow="hidden" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header Section - Same content for all themes, different background colors */}
          <Box className="orange-header-box" sx={{ 
            background: themeName === THEMES.TRADESHOW 
              ? (theme.palette.grey?.[200] || '#E5E7EB') 
              : themeName === THEMES.TICKETCOVERAGE
              ? 'linear-gradient(135deg, #1e1b4b 0%, #7c3aed 30%, #3b82f6 60%, #06b6d4 80%, #f97316 100%)'
              : themeName === THEMES.ORANGE
              ? 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)'
              : (theme.palette.primary?.main || '#1976d2'),
            color: themeName === THEMES.TRADESHOW ? theme.palette.text.primary : 'white',
            p: 3,
            position: 'relative',
            textAlign: 'left'
          }}>
            {/* Fireworks overlay behind header (only on download screen) */}
            {showCelebration && (<FireworksOverlay />)}
            {/* Main header content container */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 2, sm: 0 }
            }}>
              {/* Header text + logo - Same for all themes */}
              <Box sx={{ flex: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                      Event Ticket & <br />Trip Protection
                  </Typography>
                  {themeName === THEMES.TRADESHOW ? (
                    /* Tradeshow theme: Video logo */
                    <Box
                      component="video"
                      autoPlay
                      loop
                      muted
                      playsInline
                      sx={{
                        width: { xs: 150, sm: 250 },
                        height: 'auto',
                        borderRadius: 1,
                        ml: 2,
                        objectFit: 'contain'
                      }}
                    >
                      <source src="/data/61658-13-Horizontal.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </Box>
                  ) : (
                    /* Other themes: Static image logo */
                    <Box
                      component="img"
                      src={
                        themeName === THEMES.TICKETCOVERAGE
                          ? '/data/ticketcoveragegraphic.jpg'
                          : 'https://www.jetsetinsure.com/web/image/855-5ea4b3d6/JetSet%20-%20New%20Tagline.webp'
                      }
                      alt={
                        themeName === THEMES.TICKETCOVERAGE 
                          ? 'TicketCoverage.com' 
                          : 'JetSetInsure.com'
                      }
                      sx={{
                        width: { xs: 150, sm: 250 },
                        height: 'auto',
                        borderRadius: 1,
                        ml: 2
                      }}
                    />
                  )}
                </Box>

              </Box>

              {/* Info Badge moved below image (handled above) */}
              {/* Red gradient button removed as requested */}
            </Box>
          </Box>

          {/* Learn More Banner under header */}
          <Box className="learn-more-banner" onClick={toggleMarketing}>
            <Typography 
              variant="body1" 
              sx={{ fontWeight: 600, textAlign: 'center', width: '100%' }}
            >
              {showMarketing ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <ArrowBack sx={{ fontSize: 20, color: 'inherit' }} />
                  Go Back To My Quote
                </Box>
              ) : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline sx={{ fontSize: 20, color: 'inherit' }} />
                 Learn More About Event Ticket Protection
                </Box>
              )}
            </Typography>
          </Box>
          {/* New Trip Protection Learn More Banner */}
          <Box className="learn-more-banner" onClick={toggleTripMarketing}>
            <Typography 
              variant="body1" 
              sx={{ fontWeight: 600, textAlign: 'center', width: '100%' }}
            >
              {showTripMarketing ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <ArrowBack sx={{ fontSize: 20, color: 'inherit' }} />
                  back to Quote
                </Box>
              ) : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline sx={{ fontSize: 20, color: 'inherit' }} />
                  Learn More About Trip Protection
                </Box>
              )}
            </Typography>
          </Box>

          {/* Content Section */}
          <Box p={3}>
            {/* Marketing Channel validation message */}
            {isMccMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">Marketing Channel NOT configured</Typography>
                <Typography variant="body2">Please contact <a href="mailto:support@landsmaninsurance.com">support@landsmaninsurance.com</a>.</Typography>
              </Alert>
            )}
            {/* Section Header - Only show for Orange theme */}
            {activeStep === 0 && themeName === THEMES.ORANGE && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#E67E22', 
                      fontWeight: 'bold',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    ✈️ ON YOUR MARKS . . .
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Box sx={{ display: showMarketing ? 'block' : 'none' }}>
              <Box sx={{ 
                width: '100%',
                height: `${marketingHeight}px`,
                minHeight: '500px',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 1,
                transition: 'height 0.3s ease-in-out'
              }}>
                <Box
                  component="iframe"
                  src={marketingUrl}
                  title="Event Ticket Protection Marketing"
                  frameBorder="0"
                  allow="fullscreen"
                  scrolling="no"
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block'
                  }}
                />
              </Box>
            </Box>

            {/* Trip Protection Marketing Content */}
            <Box sx={{ display: showTripMarketing ? 'block' : 'none' }}>
              <Typography variant="h6" mb={2} textAlign="center">Learn More</Typography>
              <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  component="iframe"
                  key={showTripMarketing ? 'marketing-trip-on' : 'marketing-trip-off'}
                  src={showTripMarketing 
                    ? 'https://player.vimeo.com/video/1108849190?autoplay=1&muted=1&autopause=0&playsinline=1' 
                    : 'https://player.vimeo.com/video/1108849190'}
                  title="Learn More Video"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>

              {/* FAQs Section */}
              <Box mt={3}>
                <Typography variant="h6" textAlign="center" fontWeight="bold" mb={2}>
                  Top Questions Answered
                </Typography>
                <Box>
                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        Am I able to purchase a plan while I’m on a trip?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}><strong>In a word, no.</strong></Typography>
                      <Typography variant="body2">battleface single-trip plans for US residents may be purchased up to the day before departure, but may not be purchased while actually on a trip.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        Can I see plan documents prior to purchase?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        Sure! Just check out the sample{' '}
                        <Box component="a" href="https://partner.battleface.com/library/partner-customer-library-discovery" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>Customer Library</Box>{' '}
                        to view plan language applicable to your state.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        What’s the difference between Trip Cancellation, Trip Interruption and Trip Delay?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>Trip Cancellation covers you <strong><em>before</em></strong> your trip starts, in the event you have to cancel for a covered reason.</Typography>
                      <Typography variant="body2" mb={1}>Trip Interruption covers you <strong><em>during</em></strong> your trip if you have to return home for a covered reason. You can be reimbursed for unused arrangements, plus any extra airfare costs to return home.</Typography>
                      <Typography variant="body2">Trip Delay covers you <strong><em>during</em></strong> your trip—it can reimburse the costs of meals, local transportation, and accommodations when your trip is unexpectedly delayed for covered reasons.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        When does coverage begin and end for Trip Cancellation and Trip Interruption?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>Trip Cancellation begins at 12:01 AM the day after you purchase your plan and ends the earlier of: when you cancel your trip, or 11:59 PM the day before departure.</Typography>
                      <Typography variant="body2">Trip Interruption begins on the later of: 12:01 AM on your scheduled departure date, or the date/time you actually start the trip. It ends on the earlier of: your arrival at the return destination, or your scheduled return date.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        What if my flight is cancelled?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">It depends. If the cancellation is due to airline issues (e.g., staffing), they are responsible for rebooking and covering expenses. If it’s due to events like a hurricane, coverage could apply under Trip Cancellation, Trip Interruption, Trip Delay or Missed Connection. Document everything and contact battleface for guidance.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        What should I do if my flight is delayed?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>It depends—when the delay occurs, how long it is, and why. Different coverages could apply with different covered reasons and time frames.</Typography>
                      <Typography variant="body2" mb={1}>1) Document as much as possible, including the reason for the delay and receipts for any expenses. 2) Contact battleface to clarify benefits and navigate your situation.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        “Here’s my situation…will it be covered?”
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">It’s hard to give definitive answers to hypothetical situations because one detail can change everything. battleface will do their best to answer questions and help you understand the plan language.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        What is a pre-existing medical condition, and how can I get coverage for it?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>Many plans exclude losses resulting from pre-existing medical conditions. However, single-trip plans may offer a waiver if you buy within 15 days of your initial trip payment and insure your full trip cost.</Typography>
                      <Typography variant="body2">A pre-existing condition generally means care, diagnosis, or treatment by a physician within 180 days prior to purchasing a plan. Read your plan wording and contact battleface with questions.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        What is the Cancel For Any Reason benefit and what does it cover?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>Cancel For Any Reason allows you to cancel for any reason, even one not covered under Trip Cancellation. It’s sold as an add-on to your trip cancellation benefit.</Typography>
                      <Typography variant="body2" mb={1}>Coverage limit is 75% (or 50% for FL residents) of your original trip cost. Requirements include purchasing more than 10 days before departure, within 15 days of deposit, and insuring your full trip cost. Not available to NY residents.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        How is the required purchase period calculated for CFAR and the Pre-Existing Conditions Waiver?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">You must purchase your plan within 15 days of your initial trip deposit. Day zero is the date you first made a reservation or deposit. For example, a November 1 deposit gives you until November 16 to qualify for CFAR or the Pre-Existing Conditions Waiver (if you insure your full trip cost).</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        Do Single Trip Plans include travel assistance services?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>Yes. Non-Insurance Assistance services are provided by Robin Assist, offering 24/7 responsive customer service, claims, emergency travel and medical assistance.</Typography>
                      <Typography variant="body2" mb={1}><strong>Emergency Medical Assistance</strong> includes referrals, case management, medical payment arrangements, and emergency Rx assistance.</Typography>
                      <Typography variant="body2"><strong>Travel Assistance Services</strong> include emergency travel arrangements, embassy/consulate information, missed connections coordination, lost baggage search, and lost passport assistance.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        Are there any exclusions?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">Travel protection doesn’t cover everything and is designed for unforeseeable circumstances. Coverage varies by state. For full details, read your plan wording or visit the{' '}<Box component="a" href="https://partner.battleface.com/library/partner-customer-library-discovery" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>Customer Library</Box>.</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#E67E22', fontWeight: 600 }}>
                        How do claims work?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>battleface provides fast, responsive claims support. Document situations and keep receipts. For emergencies, contact battleface for assistance and required documentation.</Typography>
                      <Typography variant="body2">Start your claim here: <Box component="a" href="https://www.battleface.com/en-us/file-a-claim/" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>File a claim</Box>.</Typography>
                    </AccordionDetails>
                  </Accordion>

                </Box>

                {/* Footer Section: JetSetInsure & battleface disclosures */}
                <Box
                  mt={4}
                  sx={{
                    backgroundColor: '#2C1E15',
                    color: '#f2e6d8',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    boxShadow: 1,
                    '& .MuiTypography-root': { color: '#f2e6d8' }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    {/* Optional logo image; safely hidden if not available */}
                    <Box
                      component="img"
                      src={"https://www.jetsetinsure.com/web/image/855-5ea4b3d6/JetSet%20-%20New%20Tagline.webp"}
                      alt="JetSetInsure.com"
                      sx={{
                        width: 140,
                        height: 'auto',
                        borderRadius: 1,
                        display: 'block'
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        JetSetInsure.com™ is wholly owned by Landsman Insurance Services, LLC. National Producer Number: 21500715 and connects travelers with top-rated travel insurance products from leading third-party insurers. JetSetInsure.com does not underwrite insurance policies or provide claims service. Coverage decisions and claims are handled directly by the issuing insurance companies.
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        "Annual Policies" and "Single Trip Policies" are provided by battleface Insurance Services, LLC.
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        battleface Travel Insurance plans are underwritten by Spinnaker Insurance Company (an IL Corporation, NAIC# 24376), with administrative office at One Pluckemin Way, Suite 102, Bedminster, NJ 07921. Plans are offered by Landsman Insurance Services, LLC. NPN: 21500715 (California: Landsman Insurance Services LLC LICENSE NUMBER: 6016307) (Texas: Landsman Insurance Services LICENSE NUMBER: 3312498) and administered by battleface Insurance Services LLC, 800 N. High Street, Columbus, OH 43215, National Producer Number 18731960 (FL License number L107363/CA License number 0M75381). Plans available for purchase through <Box component="span" sx={{ fontWeight: 700 }}>JetSetInsure.com™</Box>
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        This is a brief description of the coverage provided under policy series RIG-1000. The Policy will contain reductions, limitations, exclusions, and termination
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        Non-insurance assistance services are provided by Robin Assist LLC (in CA: battleface Insurance Services dba Robin Assist).
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 3, opacity: 0.9 }}>
                        Copyright © 2025 Landsman Insurance Services, LLC. NPN: 21500715 - All rights reserved.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: (showMarketing || showTripMarketing) ? 'none' : 'block' }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, ml: '-13px' }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel 
                    StepIconComponent={CustomStepIcon}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box display="flex" justifyContent="center" my={2}><CircularProgress /></Box>}

           
        {/* STEP 0: RESIDENCE INFO - Country and (US-only) State selection */}
        {activeStep === 0 && (
          <Box>
            <TextField
              select
              label="Country of Residence"
              value={countryOfResidence}
              onChange={(e) => setCountryOfResidence(e.target.value)}
              fullWidth
              margin="normal"
            >
              {COUNTRY_LIST.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>

            {countryOfResidence === 'US' && (
              <TextField
                select
                label="Home State"
                value={homeCountryState}
                onChange={(e) => {
                  const stateCode = e.target.value;
                  // Check if the selected state is ineligible
                  if (stateCode === 'WA' || stateCode === 'NY') {
                    setSelectedIneligibleState(
                      stateOptions.find(state => state.code === stateCode)?.name || stateCode
                    );
                    setStateDialogOpen(true);
                  } else {
                    setHomeCountryState(stateCode);
                  }
                }}
                fullWidth
                margin="normal"
                error={!!errors.homeCountryState}
                helperText={errors.homeCountryState || "Select your home state"}
              >
                {stateOptions.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => guardedSetActiveStep(1)}
              disabled={isMccMissing || (countryOfResidence === 'US' && !homeCountryState)}
            >
              Next
            </Button>
          </Box>
        )}

        {/* STEP 1: EVENT DETAILS - Conference info and branching */}
        {activeStep === 1 && (
          <Box>
            <Box mb={3}>
              <Typography variant="h6" mb={2}>Event Details</Typography>
              {/* Event meta fields placed before dates */}
              <TextField
                label="Event Name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                fullWidth
                margin="normal"
                error={!!errors.eventName}
                helperText={errors.eventName || undefined}
              />
              <TextField
                label="Event City"
                value={eventCity}
                onChange={(e) => setEventCity(e.target.value)}
                fullWidth
                margin="normal"
                error={!!errors.eventCity}
                helperText={errors.eventCity || undefined}
              />
              <AutoCloseDatePicker
                label="Ticket Start Date"
                value={conferenceStart}
                onChange={(d) => {
                  setConferenceStart(d);
                  // Automatically set end date to 3 days after start date
                  if (d) {
                    const endDate = new Date(d);
                    endDate.setDate(endDate.getDate() + 3);
                    setConferenceEnd(endDate);
                  }
                }}
                shouldDisableDate={(date) => {
                  const today = new Date();
                  today.setHours(0,0,0,0);
                  const minStart = new Date(today);
                  minStart.setDate(minStart.getDate() + 1);
                  const cmp = new Date(date);
                  cmp.setHours(0,0,0,0);
                  return cmp < minStart; // must be at least 1 day in the future
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', required: true, error: !!errors.conferenceStart, helperText: errors.conferenceStart || undefined } }}
              />
              <AutoCloseDatePicker
                label="Ticket End Date"
                value={conferenceEnd}
                onChange={(d) => setConferenceEnd(d)}
                shouldDisableDate={(date) => {
                  if (!conferenceStart) return true; // require start first
                  const start = new Date(conferenceStart);
                  start.setHours(0,0,0,0);
                  const end = new Date(date);
                  end.setHours(0,0,0,0);
                  return end < start; // end can be same day or after
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', required: true, error: !!errors.conferenceEnd, helperText: errors.conferenceEnd || undefined } }}
              />
              <Box mt={2}>
                <Typography variant="h6" mb={1}>Registration Type</Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={attendeeRole}
                    onChange={(e) => setAttendeeRole(e.target.value)}
                  >
                    <FormControlLabel value="Exhibitor" control={<Radio />} label="Exhibitor" />
                    <FormControlLabel value="Attendee" control={<Radio />} label="Attendee" />
                  </RadioGroup>
                  {!!errors.attendeeRole && (
                    <Typography variant="caption" color="error">{errors.attendeeRole}</Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
            <TextField
              label="Event Ticket Price"
              type="number"
              fullWidth
              margin="normal"
              value={conferencePrice}
              onChange={(e) => setConferencePrice(e.target.value)}
              onWheel={(e) => e.target.blur()}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
              error={!!errors.conferencePrice}
              helperText={errors.conferencePrice || 'Enter the nonrefundable event ticket/registration cost (max $5,000)'}
            />
            <Box mb={3}>
              <Typography variant="h6" mb={2}>Ticket Purchase Date</Typography>
              <AutoCloseDatePicker
                label="Ticket Purchase Date"
                value={ticketPurchaseDate}
                onChange={(d) => setTicketPurchaseDate(d)}
                shouldDisableDate={(date) => {
                  if (!conferenceStart) return false; // allow any date selection
                  const start = new Date(conferenceStart);
                  start.setHours(0,0,0,0);
                  const purchase = new Date(date);
                  purchase.setHours(0,0,0,0);
                  return purchase >= start; // disable dates on or after start date
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', required: true, error: !!errors.ticketPurchaseDate, helperText: errors.ticketPurchaseDate || 'Select any date before the event start date' } }}
              />
            </Box>
            <TextField
              label="Traveler Age"
              type="number"
              fullWidth
              margin="normal"
              value={singleTravelerAge}
              onChange={(e) => setSingleTravelerAge(e.target.value)}
              onWheel={(e) => e.target.blur()}
              inputProps={{ min: 0, max: 120 }}
            />
            <Box mt={2}>
              <Typography variant="h6" mb={2}>Is your destination more than 100 miles from your residence?</Typography>
              <Box>
                <FormControl component="fieldset" error={!!errors.over100Miles}>
                  <RadioGroup
                    row
                    value={over100Miles === null ? '' : (over100Miles ? 'yes' : 'no')}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOver100Miles(val === 'yes');
                    }}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {!!errors.over100Miles && (
                    <Typography variant="caption" color="error">{errors.over100Miles}</Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
            <Box mt={4} mb={2}>
              <Typography variant="subtitle1" mb={1}>
                Would you like to insure just your ticket registration or your ticket registration and the rest of your travel as well?
              </Typography>
              {over100Miles === false && (
                <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                  Travel Protection is not available for trips less than 100 miles from your residence. Please proceed with Ticket Only.
                </Alert>
              )}
              <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ mt: 2 }}>
                {over100Miles !== false && (
                <Button
                  variant={insureAllTravel ? 'contained' : 'outlined'}
                  onClick={async () => {
                    setInsureAllTravel(true);
                    // Apply event details into main flow and proceed to Travel Details
                    setCfarDepositDate(ticketPurchaseDate || null);
                    setNumTravelers(1);
                    setTravelerAges([singleTravelerAge]);
                    setApplicants([{
                      first_name: '',
                      middle_name: '',
                      last_name: '',
                      email: '',
                      contact_number: '',
                      address: '',
                      city: '',
                      post_code: '',
                      country_code: countryOfResidence,
                      state_code: countryOfResidence === 'US' ? homeCountryState : '',
                      is_main: true
                    }]);
                    if (!conferenceStart || !conferenceEnd || !conferencePrice || !ticketPurchaseDate || !singleTravelerAge || isMccMissing) return;
                    // Create quote based on event details
                    await CreateEventTicketProtectionQuote();
                    // Navigate to Travel Details
                    guardedSetActiveStep(2);
                  }}
                    disabled={over100Miles === null || !conferenceStart || !conferenceEnd || !conferencePrice || !ticketPurchaseDate || !singleTravelerAge || isMccMissing}
                >
                  Ticket Plus Travel
                </Button>
                )}
                <Button
                  variant={!insureAllTravel ? 'contained' : 'outlined'}
                  onClick={async () => {
                    setInsureAllTravel(false);
                    // Apply event details into main flow and bypass to Quote
                    setTripStart(conferenceStart);
                    setTripEnd(conferenceEnd);
                    setCfarDepositDate(ticketPurchaseDate || null);
                    setNumTravelers(1);
                    setTravelerAges([singleTravelerAge]);
                    setApplicants([{
                      first_name: '',
                      middle_name: '',
                      last_name: '',
                      email: '',
                      contact_number: '',
                      address: '',
                      city: '',
                      post_code: '',
                      country_code: countryOfResidence,
                      state_code: countryOfResidence === 'US' ? homeCountryState : '',
                      is_main: true
                    }]);
                    if (!conferenceStart || !conferenceEnd || !conferencePrice || !ticketPurchaseDate || !singleTravelerAge || isMccMissing) return;
                    // Default destination to US if none
                    if (!selectedDestinations?.length) {
                      setSelectedDestinations([]);
                    }
                    // Create quote based on event details
                    await CreateEventTicketProtectionQuote();
                    // Navigate directly to Quote screen
                    guardedSetActiveStep(3);
                  }}
                  disabled={over100Miles === null || !conferenceStart || !conferenceEnd || !conferencePrice || !ticketPurchaseDate || !singleTravelerAge || isMccMissing}
                >
                  Ticket Only
                </Button>
              </Box>
            </Box>
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => guardedSetActiveStep(0)}
              disabled={isMccMissing}
            >
              Back to Residence Info
            </Button>
          </Box>
        )}

        {activeStep === 2 && (
          <form onSubmit={e => { e.preventDefault(); createQuote(); }}>
            {/* Destinations: multi-select typeahead (states + countries) */}
            <Box mb={3}>
              <Typography variant="h6" mb={2}>Where are you traveling?</Typography>
              <Autocomplete
                multiple
                options={destinationOptions}
                value={selectedDestinations}
                onChange={(_, newValue) => setSelectedDestinations(newValue || [])}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (option.type !== value.type) return false;
                  if (option.type === 'state') return option.stateCode === value.stateCode && option.countryCode === value.countryCode;
                  return option.countryCode === value.countryCode;
                }}
                filterSelectedOptions
                getOptionLabel={(opt) => opt?.label || ''}
                sx={{
                  '& .MuiAutocomplete-option.Mui-focused': {
                    bgcolor: '#E67E22',
                    color: '#fff'
                  },
                  '& .MuiAutocomplete-option[aria-selected="true"]': {
                    bgcolor: '#E67E22',
                    color: '#fff'
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    const { key, ...rest } = tagProps;
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={option.label}
                        size="small"
                        sx={{
                          bgcolor: '#E67E22',
                          color: '#fff',
                          '& .MuiChip-label': { fontWeight: 600 }
                        }}
                        deleteIcon={<Delete sx={{ color: '#FF0000' }} />}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="States/Countries/Regions Traveling To"
                    placeholder="Type to search and select multiple"
                    margin="normal"
                    fullWidth
                    error={!!errors.destinations}
                    helperText={errors.destinations || undefined}
                  />
                )}
              />
            </Box>
            {/* Trip Start Date Selection */}
            <Box mb={3}>
              <Typography variant="h6" mb={2}>When does your trip start?</Typography>              
              <AutoCloseDatePicker
                value={tripStart}
                onChange={(newDate) => {
                  setTripStart(newDate);
                }}
                shouldDisableDate={(date) => {
                  // Only allow dates from 2 days after today to 180 days from today
                  const minStart = new Date();
                  minStart.setDate(minStart.getDate() + 2);
                  minStart.setHours(0, 0, 0, 0);
                  const maxDate = new Date();
                  maxDate.setDate(maxDate.getDate() + 180);
                  return date < minStart || date > maxDate;
                }}
                defaultCalendarMonth={findFirstAllowableDate()}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    margin: 'normal', 
                    error: !!errors.tripStart, 
                    helperText: errors.tripStart || "Your plan must begin at least 2 days from today and no later than 180 days from today.",
                    placeholder: "MM/DD/YYYY"
                  } 
                }}
              />
            </Box>

            {/* Trip End Date Selection */}
            <Box mb={3}>
              <Typography variant="h6" mb={2}>When does your trip end?</Typography>
              <AutoCloseDatePicker
                value={tripEnd}
                onChange={(newDate) => {
                  setTripEnd(newDate);
                }}
                shouldDisableDate={(date) => {
                  // Ensure end date is after start date
                  if (!tripStart) return true;
                  const minEnd = new Date(tripStart);
                  minEnd.setDate(minEnd.getDate() + 1);
                  return date <= minEnd;
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    error: !!errors.tripEnd,
                    helperText: errors.tripEnd || 'Select a date after your trip start date',
                    placeholder: 'MM/DD/YYYY'
                  }
                }}
              />
            </Box>

            {/* Trip Amount - Money formatted */}
            <TextField
                                      label="Trip Amount not including the Event Ticket cost."
              type="number"
              fullWidth
              margin="normal"
              value={tripCostCoverage}
              onChange={(e) => {
                const val = e.target.value;
                setTripCostCoverage(val);
                const num = parseFloat(val);
                if (!isNaN(num) && num > 20000) {
                  showSnackbar('Maximum trip amount is $20,000', 'warning');
                }
              }}
              onWheel={(e) => e.target.blur()}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
              error={!!errors.tripAmount}
              helperText={errors.tripAmount || "Enter the total pre-paid, non-refundable trip cost"}
            />

            {/* Home State moved to Residence Info step */}

            {/* Number of Travelers - Affects pricing */}
            <TextField
              label="Number of Travelers" type="number" fullWidth margin="normal"
              inputProps={{ min: 1, max: 10 }}
              value={numTravelers} onChange={handleNumTravelersChange}
              onWheel={(e) => e.target.blur()}
              error={!!errors.numTravelers} helperText={errors.numTravelers}
            />
            
            {/* Individual Traveler Ages - Required for pricing calculations */}
            <Box mt={2}>
              <Typography variant="subtitle1">Traveler Ages</Typography>
              {Array.from({ length: numTravelers }).map((_, idx) => (
                <TextField
                  key={idx}
                  label={`Traveler ${idx + 1} Age`}
                  type="number"
                  inputProps={{ min: 0, max: 120 }}
                  value={travelerAges[idx] || ''}
                  onChange={e => handleAgeChange(idx, e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  margin="dense"
                  fullWidth
                  error={!!errors[`age${idx}`]}
                  helperText={errors[`age${idx}`]}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
            
            {/* Submit to get quote from BattleFace API */}
            <Button
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={loading || Object.keys(errors).length > 0}
              sx={themeName === THEMES.ORANGE ? { 
                mt: 3, 
                py: 2,
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #D35400 0%, #E67E22 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0px 4px 12px rgba(230, 126, 34, 0.4)',
                },
                '&:disabled': {
                  background: '#BDC3C7',
                  color: '#7F8C8D',
                }
              } : { mt: 2 }}
            >
              {themeName === THEMES.ORANGE ? '📋 GET INSTANT QUOTE' : 'Get Quote'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => guardedSetActiveStep(1)}
              disabled={isMccMissing}
            >
              Back to Event Details
            </Button>
          </form>
        )}

        {/* STEP 1: QUOTE REVIEW - Display quote results and coverage options
             This step shows the quote returned from BattleFace API including:
             - Total policy cost prominently displayed
             - Detailed breakdown of all coverage benefits and limits
             - Trip cancellation coverage selector (dropdown with 5 options)
             - Options to proceed to order or go back to edit details
             
             The coverage dropdown triggers requoteWithNewCoverage() when changed */}
        {activeStep === 3 && quote && (
          <Box>
            {/* Policy Amount Section */}
            <Box mb={4} p={3} border={1} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Total Plan Cost:
                </Typography>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {displayTotal.currency} ${displayTotal.amount.toFixed(2)}
                </Typography>
              </Box>

              {/* Travel Protection summary (only if user opted for travel protection) */}
              {insureAllTravel && (
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
                  <Box>
                    {quote.data?.trip_length && (
                      <Typography variant="body2" color="text.secondary">
                        Coverage for {quote.data.trip_length} days
                      </Typography>
                    )}
                    {quoteId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Quote Reference: {quoteId}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography
                      component="a"
                      href="https://partner.battleface.com/library/partner-customer-library-discovery"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      color="primary.main"
                      sx={{ textDecoration: 'underline', cursor: 'pointer', '&:hover': { textDecoration: 'none' } }}
                    >
                      View Sample Plan
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Event Ticket Protection summary (shown when Conference Only or when toggle is ON) */}
              {(!insureAllTravel || (insureAllTravel && eventTicketIncluded)) && (
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Coverage for Event Ticket Amount
                    </Typography>
                    {eventQuoteId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Quote Reference: {eventQuoteId}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography
                      component="a"
                      href="https://partner.battleface.com/library/bf-event-ticket"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      color="primary.main"
                      sx={{ textDecoration: 'underline', cursor: 'pointer', '&:hover': { textDecoration: 'none' } }}
                    >
                      View Sample Plan
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            

            <Typography variant="h6" mb={3} textAlign="center">Customize your plan</Typography>
            
            {/* Benefits Section - cards with toggles */}
            <Box mb={3}>
              {/* Event Ticket Protection - always first */}
              {(() => {
                // Price comes from base package in event quote response
                const eventCardPrice = (!insureAllTravel)
                  ? (typeof eventPrice === 'number' ? eventPrice : undefined)
                  : (over100Miles === false) ? 0 : ((typeof eventPrice === 'number' && eventPrice > 0) ? eventPrice : (() => {
                  const exact = detailAmounts['base_package'];
                  if (typeof exact === 'number') return exact;
                  const foundKey = Object.keys(detailAmounts).find(k => k.includes('base'));
                  return typeof detailAmounts[foundKey] === 'number' ? detailAmounts[foundKey] : undefined;
                })());
                const alias = 'event_ticket_protection';
                const label = 'Event Ticket Protection';
                const checked = eventTicketIncluded;
                return (
                  <Box key={alias} mb={2} p={2.5} border={1} borderColor="grey.300" borderRadius={3} boxShadow={1}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {BASE_ICONS[alias]}
                        <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 140, justifyContent: 'flex-end' }}>
                        {renderPriceOrAddLabel(checked, eventCardPrice)}
                        {/* Toggle hidden when destination is not over 100 miles; otherwise behaves as before */}
                        {over100Miles !== false && (
                          <Switch
                            size="small"
                            checked={checked}
                            disabled={!insureAllTravel}
                            onChange={(e) => setEventTicketIncluded(e.target.checked)}
                            inputProps={{ 'aria-label': `${label} toggle` }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          Coverage for your conference registration when events are canceled or you are unable to attend for covered reasons.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          Works alongside your trip protection to ensure your event fees are protected separately from your travel arrangements.
                        </Typography>
                      </li>
                    </Box>
                  </Box>
                );
              })()}
              {insureAllTravel && (
              <>
              {/* Trip Cancellation first */}
              {(() => {
                const alias = 'trip_cancellation_package';
                const label = 'Trip Cancellation Package';
                const limit = `Up to $${Number(tripCostCoverage || 0).toLocaleString()}`;
                const price = typeof basePriceCache[alias] === 'number' ? basePriceCache[alias] : baseAmounts[alias];
                const checked = !!baseSelections[alias];
                return (
                  <Box key={alias} mb={2} p={2.5} border={1} borderColor="grey.300" borderRadius={3} boxShadow={1}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {BASE_ICONS[alias]}
                        <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 140, justifyContent: 'flex-end' }}>
                        {renderPriceOrAddLabel(checked, price)}
                        <Switch
                          size="small"
                          checked={checked}
                          onChange={(e) => {
                            const nextChecked = e.target.checked;
                            setBaseSelections(prev => ({ ...prev, [alias]: nextChecked }));
                            if (!nextChecked && optionalSelections?.cancel_for_any_reason) {
                              setOptionalSelections(prev => ({ ...prev, cancel_for_any_reason: false }));
                              setCfarDepositDate(null);
                            }
                          }}
                          inputProps={{ 'aria-label': `${label} toggle` }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          Coverage <Box component="span" sx={{ fontWeight: 600 }}>up to 100% of your pre-paid, non-refundable trip cost</Box> if you need to cancel your trip for a covered reason.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          For instance, if <Box component="span" sx={{ fontWeight: 600 }}>you or your traveling companion become sick with COVID-19 before your departure date</Box>, Trip Cancellation will cover expenses for your unused, not refunded trip cost to a max of $20,000.00.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          Single Occupancy coverage <Box component="span" sx={{ fontWeight: 600 }}>up to 100% of trip cost</Box> if your traveling companion cancels or interrupts their trip for a covered reason and you choose to still go but have additional costs.
                        </Typography>
                      </li>
                    </Box>
                  </Box>
                );
              })()}


              {/* Remaining benefits */}
              {[{
                alias: 'trip_interruption',
                label: 'Trip Interruption',
                limit: `Up to $${Number((tripCostCoverage || 0) * 1.25).toLocaleString()}`,
                description: 'Coverage up to 125% of your pre-paid and non-refundable trip cost if a portion of a trip is missed, or if you must return home early due to unforeseen circumstances, including illness, injury, or death.'
              }, {
                alias: 'delay_package',
                label: 'Trip Delay Package',
                limit: '$200/day up to $2,500',
                description: 'Includes: Up to $200 per day and $2,500 per trip to provide coverage for the costs of meals, local transportation and accommodations when your trip is unexpectedly delayed for covered reasons. Missed Connection provides $1,000 of coverage which can cover you for the cost of extra transportation to rejoin the trip, additional local expenses, and the cost of missed arrangements.'
              }, {
                alias: 'baggage_package',
                label: 'Baggage Package',
                limit: 'Up to $2,500',
                description: 'Includes: Up to $2,500 for reimbursement of lost, stolen, or damaged luggage, sporting equipment and personal effects. Baggage Delay provides up to $100 per day or $500 per trip to cover the purchase of clothing, toiletries, and other essential items if your luggage is delayed for a specified period of time.'
              }, {
                alias: 'travel_medical_package',
                label: 'Travel Medical Package',
                limit: `Up to ${getTravelMedicalCoverage(homeCountryState).amount}`,
                description: 'Covers the costs to treat a medical emergency, including COVID-19, during a trip. $0 deductible. Includes Emergency Dental ($750) and Hospital Room & Board ($50/day).'
              }].map(({ alias, label, limit, description }) => {
                const price = typeof basePriceCache[alias] === 'number' ? basePriceCache[alias] : baseAmounts[alias];
                const checked = !!baseSelections[alias];
                return (
                  <Box key={alias} mb={2} p={2.5} border={1} borderColor="grey.300" borderRadius={3} boxShadow={1}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {BASE_ICONS[alias]}
                        <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 140, justifyContent: 'flex-end' }}>
                        {renderPriceOrAddLabel(checked, price)}
                        <Switch
                          size="small"
                          checked={checked}
                          onChange={(e) => setBaseSelections(prev => ({ ...prev, [alias]: e.target.checked }))}
                          inputProps={{ 'aria-label': `${label} toggle` }}
                        />
                      </Box>
                    </Box>
                    {/* Description below spans full width */}
                    {alias === 'trip_interruption' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              Coverage <Box component="span" sx={{ fontWeight: 600 }}>up to 150% of your pre-paid and non-refundable trip cost</Box> if a portion of a trip is missed, or if you must return home early due to unforeseen circumstances, including illness, injury, or death.
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              For instance, <Box component="span" sx={{ fontWeight: 600 }}>if you or your traveling companion become sick with COVID-19 while traveling</Box>, Trip Interruption will cover expenses like additional flight costs if you aren't cleared to travel home on your original return date.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'delay_package' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Up to $200 per day and $2,500 per trip</Box> to provide coverage for the costs of meals, local transportation and accommodations when your trip is unexpectedly delayed for covered reasons.
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Missed Connection provides $1,000 of coverage</Box> which can cover you for the cost of extra transportation to rejoin the trip, additional local expenses, and the cost of missed arrangements.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'baggage_package' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Up to $2,500 for reimbursement</Box> of lost, stolen, or damaged luggage, sporting equipment and personal effects.
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Baggage Delay provides up to $100 per day or $500 per trip</Box> to cover the purchase of clothing, toiletries, and other essential items if your luggage is delayed for a specified period of time.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'travel_medical_package' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              Coverage <Box component="span" sx={{ fontWeight: 600 }}>$100,000</Box> for the costs to treat a medical emergency, <Box component="span" sx={{ fontWeight: 600 }}>including COVID-19</Box>, during a trip.
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Emergency Medical Transportation & Repatriation of Remains</Box>, <Box component="span" sx={{ fontWeight: 600 }}>$500,000</Box>, for the transportation of a traveler to the nearest adequate medical facility or home in the event of a medical emergency during their covered trip.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    )}
                  </Box>
                );
              })}
              </>
              )}
            </Box>

            {/* Optional Add-Ons cards (no title per request) */}
            {insureAllTravel && (
            <Box mt={2} mb={4}>
              {OPTIONAL_ALIASES.map(alias => {
                const label = OPTIONAL_LABELS[alias] || alias.replace(/_/g, ' ');
                const price = typeof optionalPriceCache[alias] === 'number' ? optionalPriceCache[alias] : optionalAmounts[alias];
                const checked = !!optionalSelections[alias];
                return (
                  <Box key={alias} mb={2} p={2.5} border={1} borderColor="grey.300" borderRadius={3} boxShadow={1}>
                    {/* Header Row */}
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {OPTIONAL_ICONS[alias]}
                        <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 140, justifyContent: 'flex-end' }}>
                        {renderPriceOrAddLabel(checked, alias === 'vacation_rental_damage' && typeof price === 'number' ? (price * (vrdReservations || 1)) : price)}
                        <Switch
                          size="small"
                          checked={checked}
                          onChange={(e) => setOptionalSelections(prev => ({ ...prev, [alias]: e.target.checked }))}
                          inputProps={{ 'aria-label': `${label} toggle` }}
                        />
                      </Box>
                    </Box>

                    {/* Description */}
                    {alias === 'pet_travel_package' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Pet Medical</Box> provides <Box component="span" sx={{ fontWeight: 600 }}>$2,000 of coverage</Box> for necessary veterinarian expenses if your pet becomes sick or injured while traveling with you
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Pet Return</Box> provides <Box component="span" sx={{ fontWeight: 600 }}>$500 of coverage</Box> if you’re traveling with your pet, and can’t continue due to your sickness or injury, and your pet needs to return home.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'rental_vehicle_damage' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>Up to $35,000 for one rented vehicle per traveling party for theft or damages</Box> due to collision, vandalism, windstorm, fire, hail or flood, or any cause beyond your control while in your possession.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'vacation_rental_damage' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              Reimbursement for the cost to replace damaged personal property assigned to that rental property during a stay. Coverage is provided to all traveling companions occupying the rental property during the covered trip provided you are listed on the rental agreement.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : alias === 'accidental_death_and_dismemberment' ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Includes:</Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0, '& li': { mb: '6px' } }}>
                          <li>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={{ fontWeight: 600 }}>$100,000, $250,000, or $500,000 of coverage</Box> to cover loss of eyesight or hearing, loss of limbs and death resulting from an accident while traveling. Coverage limit is chosen by you.
                            </Typography>
                          </li>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {OPTIONAL_DESCRIPTIONS[alias]}
                      </Typography>
                    )}

                    {/* Details are always shown (no expand/collapse) */}
                    <Collapse in={true} timeout="auto">
                      {/* Per-option details */}
                      {alias === 'cancel_for_any_reason' && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={1}>Date of first trip payment</Typography>
                          <DatePicker
                            value={cfarDepositDate}
                            onChange={(newDate) => setCfarDepositDate(newDate)}
                            minDate={new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)}
                            maxDate={new Date()}
                            slotProps={{ 
                              textField: { 
                                fullWidth: true, 
                                placeholder: 'MM/DD/YYYY',
                                error: !!errors.cfarDepositDate,
                                helperText: errors.cfarDepositDate || undefined
                              } 
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Your first payment toward your trip must be within the last 15 days
                          </Typography>
                        </Box>
                      )}

                      {alias === 'accidental_death_and_dismemberment' && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={1}>Accidental Death & Dismemberment</Typography>
                          <TextField
                            select
                            value={adndLimit}
                            onChange={(e) => { setAdndLimit(Number(e.target.value)); setOptionalSelections(prev => ({ ...prev, accidental_death_and_dismemberment: true })); }}
                            fullWidth
                          >
                            {[100000, 250000, 500000].map(v => (
                              <MenuItem key={v} value={v}>{v.toLocaleString()}</MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}

                      {alias === 'vacation_rental_damage' && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={1}>Select Value</Typography>
                          <TextField
                            select
                            value={vrdLimit}
                            onChange={(e) => { setVrdLimit(Number(e.target.value)); setOptionalSelections(prev => ({ ...prev, vacation_rental_damage: true })); }}
                            fullWidth
                          >
                            {[1500, 3000, 5000].map(v => (
                              <MenuItem key={v} value={v}>{`$${v.toLocaleString()} per reservation`}</MenuItem>
                            ))}
                          </TextField>

                          <Box mt={3}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Number of rentals:</Typography>
                            <Box display="flex" gap={1}>
                              {[1, 2, 3].map(n => (
                                <Button
                                  key={n}
                                  variant={vrdReservations === n ? 'contained' : 'outlined'}
                                  size="small"
                                  onClick={() => setVrdReservations(n)}
                                >
                                  {n}
                                </Button>
                              ))}
                              <Button
                                variant={vrdReservations >= 4 ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setVrdReservations(4)}
                              >
                                4+
                              </Button>
                            </Box>
                            {vrdReservations >= 4 && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                Please contact our team to cover 4 or more rental properties or units.<br />
                                Call: +1 (855) 998 2928
                              </Alert>
                            )}
                          </Box>
                        </Box>
                      )}

                      
                    </Collapse>

                    {/* Removed Show More / Show Less controls */}
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                Toggle add-ons to update the total above.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, display: 'block', mt: 1.5 }}>
                These are only brief description(s) of the coverage(s). The policy will contain reductions, limitations, exclusion, and termination provisions. Full details of each coverage are contained in the policy document. Please read the policy carefully to fully understand the coverages, terms conditions, limits, and exclusions. If there are any conflicts or discrepancies between the descriptions above and the policy or any point is not covered above, the terms and conditions of the policy shall govern. Not all plans or coverages are available in every state. Please contact battleface for more information.
              </Typography>
            </Box>
            )}

            {/* Action Buttons */}
            <Box mt={3}>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={proceedToOrder} 
                disabled={
                  loading || 
                  isMccMissing || 
                  (optionalSelections?.vacation_rental_damage && vrdReservations >= 4)
                }
              >
                Proceed to Order
              </Button>
              <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => {
                guardedSetActiveStep(insureAllTravel ? 2 : 1);
              }} disabled={isMccMissing}>
                Back to Edit Details
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 2: ORDER INFORMATION - Collect personal details for all travelers
             This step gathers detailed personal information required for the insurance policy:
             - Personal details for each traveler (name, email, phone, address)
             - Email validation with comprehensive format checking
             - Phone number formatting (auto-formats as user types)
             - State automatically set from step 0 selection
             - Validation ensures all required fields are completed correctly
             
             When submitted, calls processOrder() to create order with BattleFace */}
        {activeStep === 4 && (
          <Box>
            {/* Order Summary Box */}
            <Box mb={4} p={3} border={2} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Your one-time payment
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {displayTotal.currency} ${displayTotal.amount.toFixed(2)}
                  </Typography>
                </Box>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setActiveStep(1)}
                  sx={{ textTransform: 'none' }}
                >
                  EDIT
                </Button>
              </Box>
              
              <Box display="flex" gap={4} mt={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Plan Start
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tripStart ? tripStart.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    }) : 'Not selected'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Plan End
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tripEnd ? tripEnd.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    }) : 'Not selected'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Travelers
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {numTravelers} {numTravelers === 1 ? 'traveler' : 'travelers'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" mb={2}>Order Information</Typography>
            <Typography variant="body2" mb={3} color="text.secondary">
              Please provide details for all travelers to complete your order.
            </Typography>

            {applicants.map((applicant, index) => (
              <Box key={index} mb={4} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                <Typography variant="subtitle1" mb={2}>
                  Traveler {index + 1} {applicant.is_main && '(Main Applicant)'}
                </Typography>
                
                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="First Name"
                    value={applicant.first_name}
                    onChange={(e) => handleApplicantChange(index, 'first_name', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_first_name`]}
                    helperText={errors[`applicant${index}_first_name`]}
                  />
                  <TextField
                    label="Middle Name"
                    value={applicant.middle_name}
                    onChange={(e) => handleApplicantChange(index, 'middle_name', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Last Name"
                    value={applicant.last_name}
                    onChange={(e) => handleApplicantChange(index, 'last_name', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_last_name`]}
                    helperText={errors[`applicant${index}_last_name`]}
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="Email"
                    type="email"
                    value={applicant.email}
                    onChange={(e) => handleApplicantChange(index, 'email', e.target.value)}
                    fullWidth
                    placeholder="example@domain.com"
                    error={!!errors[`applicant${index}_email`]}
                    helperText={errors[`applicant${index}_email`] || "Enter a valid email address"}
                  />
                  <TextField
                    label="Contact Number"
                    value={applicant.contact_number}
                    onChange={(e) => handleApplicantChange(index, 'contact_number', e.target.value)}
                    fullWidth
                    placeholder="(555) 123-4567"
                    error={!!errors[`applicant${index}_contact_number`]}
                    helperText={errors[`applicant${index}_contact_number`] || "10-digit phone number required"}
                  />
                </Box>

                <TextField
                  label="Address"
                  value={applicant.address}
                  onChange={(e) => handleApplicantChange(index, 'address', e.target.value)}
                  fullWidth
                  margin="normal"
                />

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="City"
                    value={applicant.city}
                    onChange={(e) => handleApplicantChange(index, 'city', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Postal Code"
                    value={applicant.post_code}
                    onChange={(e) => handleApplicantChange(index, 'post_code', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_post_code`]}
                    helperText={errors[`applicant${index}_post_code`]}
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    select
                    label="Country"
                    value={applicant.country_code}
                    onChange={(e) => handleApplicantChange(index, 'country_code', e.target.value)}
                    fullWidth
                  >
                    {COUNTRY_LIST.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="State"
                    value={homeCountryState}
                    fullWidth
                    disabled
                    helperText="State automatically set from Policy Details"
                  >
                    {stateOptions.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            ))}

            <Box mt={3}>
                <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={processOrder} 
                disabled={
                  loading || 
                  Object.keys(errors).length > 0 || 
                  isMccMissing ||
                  (optionalSelections?.vacation_rental_damage && vrdReservations >= 4)
                }
              >
                Proceed to Payment
              </Button>
              <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => guardedSetActiveStep(3)} disabled={isMccMissing}>
                Back to Quote Review
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 3: PAYMENT - Process payment through Stripe/BattleFace integration
             This step handles the payment processing flow:
             - Displays order summary with final pricing
             - Shows order number from step 2 (if available)
             - Renders Stripe payment form (wrapped in Elements provider)
             - Payment flow follows BattleFace/Stripe integration diagram:
               1. Create PaymentMethod with Stripe using card details
               2. Fetch PaymentIntent from BattleFace (GET request)
               3. Use client_secret to confirm payment with Stripe
               4. Handle success/error and proceed to confirmation
             
             On success, calls handlePaymentSuccess() → confirmQuote() → step 4 */}
        {activeStep === 5 && (
          <Box>
            <Typography variant="h6" mb={2}>Payment Information</Typography>
            {orderNumber && (
              <Box mb={3} p={2} bgcolor="success.50" border={1} borderColor="success.main" borderRadius={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" color="success.dark" sx={{ flexGrow: 1 }}>
                    Order Number: <strong>{orderNumber}</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenEmailDialog}
                    disabled={emailing}
                  >
                    Email
                  </Button>
                </Box>
              </Box>
            )}
            <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog}>
              <DialogTitle>Email Quote</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {`Would you like to email the quote to ${(applicants.find(a => a.is_main) || applicants[0])?.email || 'the applicant'}?`}
                </DialogContentText>
                {!((applicants.find(a => a.is_main) || applicants[0])?.email) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No applicant email is set. Please enter an email in the applicant details first.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEmailDialog} disabled={emailing}>Cancel</Button>
                <Button
                  onClick={handleConfirmEmail}
                  variant="contained"
                  disabled={emailing || !((applicants.find(a => a.is_main) || applicants[0])?.email)}
                >
                  {emailing ? 'Sending...' : 'Yes'}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Global Snackbar for email feedback */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: '100%' }}
                variant="filled"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
            <Typography variant="body2" mb={3} color="text.secondary">
              Review your order and provide payment details to complete your purchase.
            </Typography>

            {/* Order Summary */}
            <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius={2} bgcolor="grey.50">
              <Typography variant="h6" mb={2}>Order Summary</Typography>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {displayTotal.currency} ${Number(displayTotal.amount).toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Number of Travelers:</Typography>
                <Typography variant="body1">{numTravelers}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Plan Start Date:</Typography>
                <Typography variant="body1">
                  {tripStart ? tripStart.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit' 
                  }) : 'Not selected'}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {displayTotal.currency} ${Number(displayTotal.amount).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {/* Stripe Payment Form */}
            <Elements stripe={stripePromise}>
              <PaymentForm
                quote={quote}
                tripCostCoverage={tripCostCoverage}
                numTravelers={numTravelers}
                orderNumber={orderNumber}
                applicants={applicants}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                loading={loading}
                setLoading={setLoading}
                displayAmount={displayTotal.amount}
                displayCurrency={displayTotal.currency}
              />
            </Elements>

            <Box mt={3}>
              <Button variant="outlined" fullWidth onClick={() => guardedSetActiveStep(4)} disabled={isMccMissing}>
                Back to Order Information
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 4: CONFIRMATION/DOWNLOAD - Display policy confirmation and details
             This is the final step showing successful completion:
             - Success message confirming purchase
             - Detailed policy information (insured name, policy dates, premium)
             - Payment details (if available from Stripe)
             - Policy number from BattleFace confirmation
             - Order number for reference
             - Download policy button
             - Information about policy documents being emailed
             - Option to start a new quote
             
             This step is reached automatically after successful payment and confirmQuote() call */}
        {activeStep === 6 && confirmation && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" component="div" fontWeight="bold">
                🎉 Purchase Confirmed!
              </Typography>
              <Typography variant="body2" mt={1}>
                Your travel insurance policy has been successfully purchased and is now active.
              </Typography>
            </Alert>
            
            {/* Policy Details Card */}
            <Box mb={4} p={3} border={2} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Typography variant="h6" fontWeight="bold" color="primary.main" mb={3} textAlign="center">
                Plan Details
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Insured Name */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Insured Name:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {applicants[0]?.first_name} {applicants[0]?.last_name}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy Number */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Policy Number:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {confirmation.data.items[0].policy_number}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Purchase Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Purchase Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    })}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy Start Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Plan Start Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {tripStart ? tripStart.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Not available'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy End Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Plan End Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {tripEnd ? tripEnd.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Not available'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Premium Paid */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Premium Paid:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {quote?.data?.currency_id || 'USD'} ${quote?.data?.quoted_price?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Coverage Amount */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Single Trip Cancellation Limit:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ${tripCostCoverage?.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Payment Success Info */}
            {paymentIntent && (
              <Box mb={3} p={2} bgcolor="success.50" border={1} borderColor="success.main" borderRadius={1}>
                <Typography variant="body2" color="success.dark">
                  <strong>✅ Payment Successful</strong><br />
                  Payment ID: {paymentIntent.id}<br />
                  Amount: ${(paymentIntent.amount / 100).toFixed(2)} {paymentIntent.currency.toUpperCase()}
                </Typography>
              </Box>
            )}
            
            {/* Order Number */}
            {orderNumber && (
              <Box mb={3} p={2} bgcolor="info.50" border={1} borderColor="info.main" borderRadius={1}>
                <Typography variant="body2" color="info.dark">
                  <strong>Order Reference:</strong> {orderNumber}
                </Typography>
              </Box>
            )}

            {/* Download Policy Button */}
            <Box mt={3} mb={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={async () => {
                  try {
                    const policyNumber = confirmation.data.items[0].policy_number;
                    // console.log('Attempting to get policy file URL for policy:', policyNumber, 'order:', orderNumber);
                    
                    // Call the new API endpoint to get the file URL
                    const response = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderNumber}/policies/${policyNumber}/files`, {
                      method: 'GET',
                      headers: {
                        'Authorization': BATTLEFACE_API_TOKEN
                      }
                    });

                    // console.log('Files API response status:', response.status);

                    if (!response.ok) {
                      throw new Error(`Failed to get policy file URL (Status: ${response.status}). Policy files may not be ready yet.`);
                    }

                    const data = await response.json();
                    // console.log('Files API response data:', data);

                    // Check if we have a valid response with file data
                    if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
                      throw new Error('No policy files found in response.');
                    }

                    // Get the first file's URL (assuming the policy file is the first one)
                    const fileUrl = data.data[0].url;
                    if (!fileUrl) {
                      throw new Error('No download URL found in file data.');
                    }

                    // console.log('Using file URL for download:', fileUrl);

                    // Fetch the actual file using the provided URL
                    const fileResponse = await fetch(fileUrl, {
                      method: 'GET',
                      headers: {
                        'Authorization': BATTLEFACE_API_TOKEN
                      }
                    });

                    if (!fileResponse.ok) {
                      throw new Error(`Failed to download file from URL (Status: ${fileResponse.status}).`);
                    }

                    // Create a blob from the file response and trigger download
                    const blob = await fileResponse.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = `policy-${policyNumber}.pdf`; // Use the file name from response if available
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                  } catch (error) {
                    console.error('Policy download error:', error);
                    setError(`Failed to download policy: ${error.message}. Please try again later or contact support.`);
                  }
                }}
                sx={{ fontWeight: 600 }}
              >
                Download Policy
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" mt={2}>
              A confirmation email will be sent to your registered email address with your policy documents.
            </Typography>
            
            {/* Add more confirmation details as needed */}
            <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => window.location.reload()}>
              Start New Quote
            </Button>
          </Box>
        )}
        </Box>
        </Box>
        </Box>
        
        {/* Dialog for past trip start date on rehydration */}
        <Dialog
          open={pastDateDialogOpen}
          onClose={() => setPastDateDialogOpen(false)}
          aria-labelledby="past-date-dialog-title"
          aria-describedby="past-date-dialog-description"
        >
          <DialogTitle id="past-date-dialog-title">
            Trip Start Date Passed
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="past-date-dialog-description">
              {pastDateMessage || 'Your trip start date has already passed. Please start a new quote.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPastDateDialogOpen(false)} color="primary" autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for ineligible states (Washington and New York) */}
        <Dialog
          open={stateDialogOpen}
          onClose={() => setStateDialogOpen(false)}
          aria-labelledby="ineligible-state-dialog-title"
          aria-describedby="ineligible-state-dialog-description"
        >
          <DialogTitle id="ineligible-state-dialog-title">
            Coverage Not Available
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="ineligible-state-dialog-description">
              We're sorry, but this coverage is not available for residents of {selectedIneligibleState}.
              <Box mt={2}>
                For more information about coverage exclusions and ineligible destinations, please click here:
              </Box>
              <Box 
                component="a" 
                href="https://library.battleface.com/ineligible-destinations/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'block', 
                  mt: 2, 
                  color: 'primary.main',
                  fontWeight: 'medium',
                  textDecoration: 'underline'
                }}
              >
                Ineligible Destinations
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStateDialogOpen(false)} color="primary" autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </ThemeProvider>
  );
}